import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import * as XLSX from "xlsx";
import {
  Edit,
  Delete,
  LockReset,
  Lock,
  Search,
  Close,
  Download,
  TrendingUp,
  People,
  Storage,
  Email,
  Send,
  Visibility,
  ViewModule,
  ViewList,
  ViewComfy,
  Upload,
  FileUpload,
  Menu as MenuIcon,
  ToggleOn,
  ToggleOff,
  CheckCircle,
  Cancel,
  HistoryRounded as HistoryRoundedIcon,
  ScheduleRounded as ScheduleIcon,
  ChevronRight,
  Stars,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";
import emailService from "../../services/emailService";
import { emailTemplates, renderTemplate } from "../../utils/emailTemplates";
import { fillBirthdayTemplate, BIRTHDAY_TEMPLATE_URL, BIRTHDAY_TEMPLATE_CANVAS } from "../../utils/birthdayTemplate";
import {
  fillAnniversaryTemplate,
  ANNIVERSARY_TEMPLATE_URL,
  ANNIVERSARY_TEMPLATE_CANVAS,
  calcularAniosDeServicio,
} from "../../utils/anniversaryTemplate";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import "./adminPanel.scss";
// Todas las fechas del panel se leen y se pintan en hora de Colombia,
// la misma en la que el backend las registra. Ver utils/fechas.js.
import { aInstante, formatearFechaHora } from "../../utils/fechas";

import { API_BASE_URL as API, urlDeArchivo } from "../../config";

// Mensaje legible de una respuesta fallida. Un texto fijo como "No se pudo
// cargar" no deja saber si el servidor está desactualizado (404), la sesión
// caducó (401/403) o falló la base de datos (500, con su código).
const mensajeDeError = async (res, porDefecto) => {
  const body = await res.json().catch(() => ({}));
  if (res.status === 404) {
    return `${porDefecto}: el servidor no tiene esta función. Hay que actualizar y reiniciar el backend.`;
  }
  if (res.status === 401 || res.status === 403) {
    return `${porDefecto}: la sesión caducó o no tiene permisos de administrador. Vuelve a iniciar sesión.`;
  }
  return body.error || body.message || `${porDefecto} (error ${res.status})`;
};

// Todas las llamadas del panel de administración van a rutas que ahora
// exigen sesión de administrador en el backend: se adjunta el token en cada
// petición sin tener que tocar cada llamada una por una.
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

/**
 * Vista previa fiel de una plantilla de correo basada en la imagen del
 * diseñador (cumpleaños y aniversario). Cada una tiene su propio tamaño de
 * lienzo, así que se renderiza a tamaño completo dentro de un <iframe> y se
 * escala visualmente para que quepa en el panel, en vez de reimplementar el
 * diseño con otro layout que enseguida queda desactualizado respecto al que
 * de verdad se envía.
 */
const TarjetaPreview = ({ html, canvas, titulo }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const updateScale = () => {
      const width = el.offsetWidth;
      if (width > 0) setScale(width / canvas.width);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    return () => observer.disconnect();
  }, [canvas.width]);

  const scaledHeight = canvas.height * scale;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: scaledHeight || undefined,
        overflow: "hidden",
        borderRadius: "10px",
        border: "1px solid #e5e9ee",
        background: "#fff",
      }}
    >
      {html && (
        <iframe
          title={titulo}
          srcDoc={html}
          scrolling="no"
          style={{
            width: canvas.width,
            height: canvas.height,
            border: "none",
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            display: "block",
            pointerEvents: "none", // es una vista previa, no un correo interactivo
          }}
        />
      )}
    </div>
  );
};

/**
 * Plantillas de correo que el panel puede previsualizar y enviar.
 *
 * La de "Cambio de contraseña" ya no está: el recordatorio automático cada
 * 90 días se retiró del backend, así que dejar aquí su plantilla solo
 * ofrecía un botón que no correspondía a ninguna automatización real.
 */
const PLANTILLAS = [
  { id: "welcome", etiqueta: "Bienvenida" },
  { id: "birthday", etiqueta: "Cumpleaños" },
  { id: "anniversary", etiqueta: "Aniversario" },
  { id: "mention", etiqueta: "Mención" },
  { id: "publication", etiqueta: "Publicación" },
];

const etiquetaPlantilla = (id) =>
  PLANTILLAS.find((p) => p.id === id)?.etiqueta || id;

/**
 * Nombres legibles de lo que guarda la bitácora. El backend anota códigos
 * cortos y estables (`usuario_creado`) para que la consulta sea fácil; la
 * traducción a español vive acá, donde se lee.
 */
const ACCIONES_LEGIBLES = {
  login: "Ingreso correcto",
  login_fallido: "Ingreso fallido",
  login_bloqueado: "Cuenta inactiva",
  usuario_creado: "Colaborador creado",
  usuario_eliminado: "Colaborador eliminado",
  usuario_activado: "Colaborador activado",
  usuario_desactivado: "Colaborador desactivado",
  usuarios_importados: "Importación de colaboradores",
  puntos_cargados: "Carga de puntos",
  contrasena_reseteada: "Contraseña restablecida",
  contrasena_cambiada: "Contraseña cambiada",
  espacio_creado: "Espacio creado",
  espacio_editado: "Espacio editado",
  espacio_eliminado: "Espacio eliminado",
  encargado_espacios: "Encargados de espacio",
  config_celebracion_guardada: "Configuración de celebraciones",
};

/** Pestañas del panel. El orden es el del menú lateral. */
const VISTAS = ["dashboard", "users", "spaces", "notifications", "logs"];
const CLAVE_VISTA = "assamas:adminView";

/**
 * Formatea una fecha "YYYY-MM-DD" sin pasar por new Date(cadena): el
 * navegador la interpretaría como UTC y en Colombia mostraría el día
 * anterior. Es el mismo criterio que usa el perfil.
 */
const MESES_CORTOS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

const formatearFechaCorta = (valor) => {
  if (!valor) return null;
  const partes = String(valor).slice(0, 10).split("-").map(Number);
  if (partes.length !== 3 || !partes[0] || !partes[1] || !partes[2]) return null;
  const [anio, mes, dia] = partes;
  return `${dia} de ${MESES_CORTOS[mes - 1]} de ${anio}`;
};

/**
 * Maqueta de un correo para la vista previa.
 *
 * Las plantillas reales las genera el backend con estilos en línea (es lo
 * único que entienden Outlook y Gmail). Aquí solo se reproduce su estructura
 * —cabecera de marca, cuerpo, recuadro destacado y botón— con las clases de
 * adminPanel.scss. Antes cada plantilla repetía su propio muro de estilos en
 * línea, unas 40 líneas por plantilla, con los colores escritos a mano.
 */
const MailMock = ({ titulo, subtitulo, children, destacado, cta }) => (
  <div className="mailMock">
    <div className="mailMock__header">
      <h2>{titulo}</h2>
      {subtitulo && <p>{subtitulo}</p>}
    </div>
    <div className="mailMock__body">
      {children}
      {destacado && <div className="mailMock__destacado">{destacado}</div>}
      {cta && <span className="mailMock__cta">{cta}</span>}
    </div>
  </div>
);

export default function AdminPanel() {
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const { isMobile, isTablet } = useMediaQuery();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // La pestaña abierta se recuerda: al recargar (F5, cambio de contraseña,
  // sesión que se refresca) el panel volvía siempre al Dashboard y había que
  // navegar otra vez hasta donde se estaba trabajando.
  const [view, setView] = useState(() => {
    try {
      const guardada = localStorage.getItem(CLAVE_VISTA);
      return VISTAS.includes(guardada) ? guardada : "dashboard";
    } catch {
      return "dashboard";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_VISTA, view);
    } catch {
      // Navegador con el almacenamiento bloqueado: no es motivo para romper
      // el panel, simplemente no se recuerda la pestaña.
    }
  }, [view]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [usersPage, setUsersPage] = useState(0);

  // Notificaciones
  const [selectedTemplate, setSelectedTemplate] = useState("welcome");
  const [selectedSpace, setSelectedSpace] = useState("");
  const [previewData, setPreviewData] = useState({
    nombre: "Juan Pérez",
    usuario: "jperez",
    password: "123456",
    cargo: "Especialista",
    // Los dos campos que pide la tarjeta de aniversario, además del nombre
    // y la foto: el área se imprime en su casilla y de la fecha de ingreso
    // salen los años cumplidos.
    area: "Recursos Humanos",
    fechaIngreso: "",
    years: 5,
    mentionedBy: "Carlos Rodríguez",
    spaceName: "General",
    postTitle: "Actualización del Espacio",
    postContent: "Se publicó una actualización importante para el equipo."
  });

  // Plantilla real de cumpleaños (frontend/public/assets/birthdaygifts/
  // birthday-email-template.html): se carga una sola vez y se rellena con
  // previewData para mostrar en "Vista Previa" exactamente lo que recibirá
  // el colaborador, en vez de una maqueta genérica aparte.
  const [birthdayTemplateRaw, setBirthdayTemplateRaw] = useState(null);
  useEffect(() => {
    fetch(BIRTHDAY_TEMPLATE_URL)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error("No se pudo cargar la plantilla"))))
      .then(setBirthdayTemplateRaw)
      .catch((err) => console.error("Error cargando plantilla de cumpleaños:", err));
  }, []);

  const birthdayPreviewHtml = useMemo(() => {
    if (!birthdayTemplateRaw) return null;

    // La fecha de la tarjeta es la de nacimiento del colaborador cargado en
    // la vista previa; si no hay ninguna (datos de ejemplo), se usa la de
    // hoy, que es lo que hacía siempre antes.
    const base = previewData.fechaNacimiento
      ? (() => {
          const [a, m, d] = String(previewData.fechaNacimiento).slice(0, 10).split("-").map(Number);
          return a && m && d ? new Date(a, m - 1, d) : new Date();
        })()
      : new Date();

    return fillBirthdayTemplate(birthdayTemplateRaw, {
      employeeName: previewData.nombre,
      employeePosition: previewData.cargo,
      employeePhoto: previewData.profilePicture || "",
      birthdayDay: String(base.getDate()).padStart(2, "0"),
      birthdayMonth: new Intl.DateTimeFormat("en-US", { month: "short" }).format(base).toUpperCase(),
      birthdayMessage:
        previewData.birthdayMessage ||
        "Desde ASSA ABLOY celebramos tu vida y el impacto positivo que generas cada día con tu talento y compromiso.",
    });
  }, [birthdayTemplateRaw, previewData.nombre, previewData.cargo, previewData.profilePicture, previewData.birthdayMessage, previewData.fechaNacimiento]);

  // Plantilla real de aniversario (misma carpeta y mismo planteamiento que
  // la de cumpleaños: la imagen del diseñador de fondo con los datos
  // dinámicos encima). Ver frontend/src/utils/anniversaryTemplate.js.
  const [anniversaryTemplateRaw, setAnniversaryTemplateRaw] = useState(null);
  useEffect(() => {
    fetch(ANNIVERSARY_TEMPLATE_URL)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error("No se pudo cargar la plantilla"))))
      .then(setAnniversaryTemplateRaw)
      .catch((err) => console.error("Error cargando plantilla de aniversario:", err));
  }, []);

  // Años que se pintan en la tarjeta: se calculan de la fecha de ingreso
  // real del colaborador y, si no la tiene cargada, se cae al valor de
  // ejemplo para que la vista previa nunca quede en blanco.
  const aniosAniversario = useMemo(() => {
    const calculado = calcularAniosDeServicio(previewData.fechaIngreso);
    return calculado ?? previewData.years ?? 1;
  }, [previewData.fechaIngreso, previewData.years]);

  const anniversaryPreviewHtml = useMemo(() => {
    if (!anniversaryTemplateRaw) return null;
    return fillAnniversaryTemplate(anniversaryTemplateRaw, {
      employeeName: previewData.nombre,
      employeeArea: previewData.area,
      employeeYears: aniosAniversario,
      employeePhoto: previewData.profilePicture || "",
    });
  }, [anniversaryTemplateRaw, previewData.nombre, previewData.area, previewData.profilePicture, aniosAniversario]);

  // Carga un colaborador REAL escogido al azar en la vista previa, con su
  // nombre, cargo, área, foto y fechas. Sirve para ver cómo va a quedar la
  // tarjeta con datos de verdad (nombres largos, áreas largas, gente con
  // foto y sin foto) antes de programar el envío — y el correo de prueba
  // sale con esos mismos datos, así que la prueba es fiel.
  const [preveOrigen, setPreveOrigen] = useState(null);
  const cargarColaboradorAlAzar = () => {
    const candidatos = (users || []).filter((u) => u.name && u.estado !== "inactivo");
    if (!candidatos.length) {
      setPreveOrigen({ error: "No hay colaboradores cargados para previsualizar." });
      return;
    }

    // El sorteo es solo entre quienes tienen foto de perfil: es lo que se
    // quiere ver en la tarjeta. Antes se exigía además el cargo, y como
    // pocos lo tienen cargado el sorteo caía casi siempre en la misma
    // persona. Si nadie tiene foto, vale cualquiera.
    const conFoto = candidatos.filter((u) => String(u.profilePicture || "").trim());

    // En aniversario la tarjeta muestra los AÑOS, que salen de la fecha de
    // ingreso: sortear a alguien que no la tenga cargada daría una tarjeta
    // con el número de ejemplo, que es justo lo que no se quiere revisar.
    // Se va cediendo requisito a requisito antes que quedarse sin nadie.
    const completos = esAniversario
      ? conFoto.filter((u) => u.fechaIngreso)
      : conFoto;

    const pool = completos.length
      ? completos
      : (conFoto.length ? conFoto : candidatos);

    // Sin repetir a quien ya se está mostrando, si hay alguien más.
    const otros = pool.length > 1 ? pool.filter((u) => u.id !== preveOrigen?.id) : pool;
    const elegido = otros[Math.floor(Math.random() * otros.length)];

    setPreviewData((prev) => ({
      ...prev,
      nombre: elegido.name,
      cargo: elegido.cargo || "",
      area: elegido.area || "",
      profilePicture: elegido.profilePicture || "",
      fechaNacimiento: elegido.fechaNacimiento || "",
      fechaIngreso: elegido.fechaIngreso || "",
      email: elegido.email || prev.email,
      usuario: elegido.username || prev.usuario,
      years: calcularAniosDeServicio(elegido.fechaIngreso) ?? prev.years,
    }));
    setPreveOrigen({
      id: elegido.id,
      nombre: elegido.name,
      conFoto: conFoto.length > 0,
      total: pool.length,
      completo: completos.length > 0,
      sinArea: !elegido.area,
      sinIngreso: !elegido.fechaIngreso,
      error: null,
    });
  };
  const [emailConfig, setEmailConfig] = useState({
    // Lo que queda aquí es solo la configuración del aviso automático de
    // publicaciones. Los campos del antiguo envío masivo (destinatarios,
    // copias, importancia, filtro de usuarios, años hito) se movieron a la
    // configuración de celebraciones, que sí se guarda en el backend.
    distributionType: "space_users",
    publicationActive: false
  });

  // Envío de prueba: manda la plantilla seleccionada, con datos de ejemplo,
  // a un único correo. Nunca toca a los colaboradores reales.
  const [testSendEmail, setTestSendEmail] = useState("");
  const [testSendLoading, setTestSendLoading] = useState(false);
  const [testSendResult, setTestSendResult] = useState(null);

  const handleSendTestEmail = async () => {
    if (!testSendEmail.trim()) {
      setTestSendResult({ ok: false, message: "Escribe un correo destino" });
      return;
    }
    setTestSendLoading(true);
    setTestSendResult(null);
    try {
      const res = await authFetch(`${API}/api/email/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateType: selectedTemplate === "birthday" ? "birthday_file" : selectedTemplate,
          recipientEmail: testSendEmail.trim(),
          testData: previewData,
        }),
      });
      const body = await res.json();
      setTestSendResult({
        ok: res.ok && body.success,
        message: body.message || (res.ok ? "Correo de prueba enviado" : "No se pudo enviar el correo de prueba"),
      });
    } catch (err) {
      setTestSendResult({ ok: false, message: err.message || "Error de red" });
    } finally {
      setTestSendLoading(false);
    }
  };

  // Historial de correos enviados: qué se envió, a quién, con copia a quién
  // y una vista previa exacta del HTML que salió. Vive en la pestaña
  // "Registros" del panel: es información de consulta, no de configuración,
  // y sacarla de "Notificaciones" deja esa pantalla en lo que de verdad se
  // manipula (elegir plantilla, destinatarios y enviar).
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [emailHistoryError, setEmailHistoryError] = useState("");
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [showHistoryPreview, setShowHistoryPreview] = useState(false);

  const fetchEmailHistory = async () => {
    setEmailHistoryLoading(true);
    setEmailHistoryError("");
    try {
      const res = await authFetch(`${API}/api/email/history?limit=100`);
      if (!res.ok) throw new Error("No se pudo cargar el historial de correos");
      const body = await res.json();
      setEmailHistory(Array.isArray(body) ? body : []);
    } catch (err) {
      setEmailHistoryError(err.message || "No se pudo cargar el historial de correos");
    } finally {
      setEmailHistoryLoading(false);
    }
  };

  // ------------------------------------------------ movimientos de puntos
  // Bitácora global de recargas y descuentos de puntos Yale: quién, cuánto,
  // por qué y quién lo cargó. Es la contraparte de lo que cada colaborador
  // ve en su billetera, pero de toda la organización.
  const [pointsLog, setPointsLog] = useState([]);
  const [pointsLogLoading, setPointsLogLoading] = useState(false);
  const [pointsLogError, setPointsLogError] = useState("");
  // Bitácora del sistema: accesos (quién entró y desde dónde) y acciones de
  // administración (altas, bajas, cambios de contraseña, cargas de puntos).
  // La escribe el backend en `system_activity_log` desde las rutas que de
  // verdad hacen el cambio, no desde aquí.
  const [activityLog, setActivityLog] = useState({ acceso: [], accion: [] });
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");

  const fetchActivityLog = async (categoria) => {
    setActivityLoading(true);
    setActivityError("");
    try {
      const res = await authFetch(`${API}/api/logs?category=${categoria}&limit=200`);
      if (!res.ok) throw new Error(await mensajeDeError(res, "No se pudo cargar la bitácora del sistema"));
      const body = await res.json();
      setActivityLog((prev) => ({ ...prev, [categoria]: Array.isArray(body) ? body : [] }));
    } catch (err) {
      setActivityError(err.message || "No se pudo cargar la bitácora del sistema");
    } finally {
      setActivityLoading(false);
    }
  };

  const [logsTab, setLogsTab] = useState("emails"); // emails | points | acceso | accion

  // Cumpleaños y aniversario son las dos únicas plantillas que se envían en
  // lote desde este panel; el resto se disparan solas (bienvenida, mención,
  // publicación). Distinguirlas aquí evita repetir la condición seis veces.
  const esCelebracion =
    selectedTemplate === "birthday" || selectedTemplate === "anniversary";

  // Las dos plantillas que se pintan sobre la imagen del diseñador piden
  // datos distintos del colaborador: cumpleaños usa la fecha de nacimiento
  // y el cargo; aniversario, el área y la fecha de ingreso.
  const esAniversario = selectedTemplate === "anniversary";

  // ------------------------------------- configuración de celebraciones
  // Cumpleaños y aniversario se envían solos el día que toca. Lo que se
  // decide aquí (a quién, con qué copias, qué años cuentan) se guarda en el
  // backend y es lo que lee el proceso diario; esta pantalla no envía nada.
  const CONFIG_CELEBRACION_VACIA = {
    isActive: true,
    audience: "celebrant",
    recipientUserIds: [],
    toEmails: "",
    ccEmails: "",
    bccEmails: "",
    milestoneYears: [],
  };

  const [celebConfig, setCelebConfig] = useState(CONFIG_CELEBRACION_VACIA);
  const [celebConfigSaving, setCelebConfigSaving] = useState(false);
  const [celebConfigMsg, setCelebConfigMsg] = useState(null);

  useEffect(() => {
    if (!esCelebracion) return;
    let cancelado = false;

    setCelebConfigMsg(null);
    authFetch(`${API}/api/email/celebration-config?templateType=${selectedTemplate}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("No se pudo leer la configuración"))))
      .then((datos) => {
        if (cancelado) return;
        setCelebConfig({
          isActive: datos.isActive !== false,
          audience: datos.audience || "celebrant",
          recipientUserIds: Array.isArray(datos.recipientUserIds) ? datos.recipientUserIds : [],
          toEmails: datos.toEmails || "",
          ccEmails: datos.ccEmails || "",
          bccEmails: datos.bccEmails || "",
          milestoneYears: Array.isArray(datos.milestoneYears) ? datos.milestoneYears : [],
        });
      })
      .catch(() => {
        // Sin configuración guardada todavía: se arranca con los valores por
        // defecto, que son el comportamiento histórico.
        if (!cancelado) setCelebConfig(CONFIG_CELEBRACION_VACIA);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, esCelebracion]);

  const guardarConfigCelebracion = async () => {
    setCelebConfigSaving(true);
    setCelebConfigMsg(null);
    try {
      const res = await authFetch(`${API}/api/email/celebration-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateType: selectedTemplate,
          ...celebConfig,
          spaceId: selectedSpace === "" ? null : Number(selectedSpace),
        }),
      });
      if (!res.ok) throw new Error(await mensajeDeError(res, "No se pudo guardar la configuración"));

      setCelebConfigMsg({
        ok: true,
        texto: celebConfig.isActive
          ? "Configuración guardada. El sistema usará estos destinatarios en el próximo envío automático."
          : "Configuración guardada. El envío automático queda desactivado hasta que lo vuelvas a activar.",
      });
    } catch (err) {
      setCelebConfigMsg({ ok: false, texto: err.message || "No se pudo guardar la configuración" });
    } finally {
      setCelebConfigSaving(false);
    }
  };

  const fetchPointsLog = async () => {
    setPointsLogLoading(true);
    setPointsLogError("");
    try {
      const res = await authFetch(`${API}/api/users/points/movements?limit=200`);
      if (!res.ok) throw new Error("No se pudo cargar el historial de puntos");
      const body = await res.json();
      setPointsLog(Array.isArray(body) ? body : []);
    } catch (err) {
      setPointsLogError(err.message || "No se pudo cargar el historial de puntos");
    } finally {
      setPointsLogLoading(false);
    }
  };

  // Cada solapa carga lo suyo al abrirse: traer las cuatro bitácoras de
  // golpe son cuatro consultas de 200 filas para mirar una sola.
  useEffect(() => {
    if (view !== "logs") return;
    if (logsTab === "emails") fetchEmailHistory();
    else if (logsTab === "points") fetchPointsLog();
    else fetchActivityLog(logsTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, logsTab]);

  const openHistoryPreview = async (id) => {
    setShowHistoryPreview(true);
    setHistoryDetail(null);
    setHistoryDetailLoading(true);
    try {
      const res = await authFetch(`${API}/api/email/history/${id}`);
      if (!res.ok) throw new Error("No se pudo cargar el detalle del correo");
      const body = await res.json();
      setHistoryDetail(body);
    } catch (err) {
      setHistoryDetail({ error: err.message || "No se pudo cargar el detalle del correo" });
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  /**
   * El HTML que se guarda de un correo de celebración es solo
   * `<img src="cid:birthday_card">`: la tarjeta viaja como ADJUNTO, y una
   * referencia "cid:" únicamente resuelve dentro del cliente de correo. En
   * un <iframe> del navegador no apunta a nada, así que el historial mostraba
   * un icono de imagen rota, como si el envío hubiera salido mal.
   *
   * Se sustituye por una nota que explica qué pasó de verdad.
   */
  const htmlParaVistaPrevia = (html) => {
    if (!html || !/src=["']cid:/i.test(html)) return html;
    return html.replace(
      /<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi,
      `<div style="font-family:Arial,Helvetica,sans-serif;border:1px dashed #c3d3e2;border-radius:10px;
                   padding:28px 20px;color:#5b6b7d;background:#f7fafd;max-width:520px;margin:0 auto;">
         <p style="margin:0 0 6px;font-weight:700;color:#005fa3;">La tarjeta viajó como imagen adjunta</p>
         <p style="margin:0;font-size:13px;line-height:1.6;">
           Se entregó dentro del correo, incrustada en el mensaje. No se puede
           mostrar aquí porque el adjunto solo existe dentro del correo enviado.
           Para ver cómo queda, usa la vista previa de la plantilla en
           <strong>Notificaciones</strong>.
         </p>
       </div>`
    );
  };

  const closeHistoryPreview = () => {
    setShowHistoryPreview(false);
    setHistoryDetail(null);
  };

  // Barra del sorteo, compartida por las dos tarjetas (cumpleaños y
  // aniversario). Además de decir a quién se está mostrando, avisa cuando a
  // esa persona le falta un dato que la tarjeta necesita: así se entiende
  // por qué un hueco sale vacío en vez de parecer un error de la plantilla.
  const bloqueAleatorio = (
    <div className="previewAleatorio">
      <button type="button" className="previewAleatorioBtn" onClick={cargarColaboradorAlAzar}>
        Probar con un colaborador al azar
      </button>

      {preveOrigen?.error && (
        <span className="previewAleatorioNota error">{preveOrigen.error}</span>
      )}

      {preveOrigen && !preveOrigen.error && (
        <span className="previewAleatorioNota">
          Mostrando a <strong>{preveOrigen.nombre}</strong>
          {" · "}
          {preveOrigen.conFoto
            ? `sorteado entre ${preveOrigen.total} ${preveOrigen.total === 1 ? "colaborador" : "colaboradores"}${
                esAniversario && preveOrigen.completo ? " con foto y fecha de ingreso" : " con foto"
              }`
            : "nadie tiene foto de perfil todavía: se usa la genérica"}
          {esAniversario && preveOrigen.sinIngreso && (
            <em> · sin fecha de ingreso cargada: los años son los de ejemplo</em>
          )}
          {esAniversario && preveOrigen.sinArea && (
            <em> · sin área cargada: esa casilla sale vacía</em>
          )}
        </span>
      )}
    </div>
  );

  const EMAIL_TEMPLATE_LABELS = {
    welcome: "Bienvenida",
    birthday: "Cumpleaños",
    birthday_file: "Cumpleaños",
    birthday_raw: "Cumpleaños",
    anniversary: "Aniversario",
    passwordreset: "Restablecer Contraseña",
    mention: "Mención",
    publication: "Publicación",
  };

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    username: "",
    documento: "",
    email: "",
    phone: "",
    country: "",
    rol: "empleado",
    direccionLaboral: "",
    linkedIn: "",
    fechaIngreso: "",
    fechaNacimiento: "",
    sendWelcomeEmail: true,
  });
  const [createError, setCreateError] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: "", message: "", icon: "" });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdUserId, setPwdUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChanging, setPasswordChanging] = useState(false);

  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  const [showOperationModal, setShowOperationModal] = useState(false);
  const [operationModalData, setOperationModalData] = useState({
    title: "",
    message: "",
    isError: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [usersViewLayout, setUsersViewLayout] = useState(isMobile ? "list" : "cards"); // 'cards', 'list', 'grid'
  const [dashboardViewLayout, setDashboardViewLayout] = useState(isMobile ? "compact" : "widgets"); // 'widgets', 'list', 'compact'

  // Import Users Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  // "full": importa/actualiza usuarios completos (requiere Nombre, Apellido,
  // Documento, Puntos, Correo). "pointsOnly": recarga rápida de puntos Yale
  // para usuarios ya existentes, solo requiere Documento + Puntos.
  const [importMode, setImportMode] = useState("full");

  // Export Metrics Modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState({
    generalMetrics: true,
    roleDistribution: true,
    spaceActivity: true,
    trafficData: true,
    areaDistribution: true,
    ageDistribution: true,
  });

  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [editingSpaceId, setEditingSpaceId] = useState(null);
  const [spaceForm, setSpaceForm] = useState({ name: "", description: "", imageFile: null, imagePreview: "" });
  const [showDeleteSpaceModal, setShowDeleteSpaceModal] = useState(false);
  const [deleteSpaceId, setDeleteSpaceId] = useState(null);
  const [deleteSpaceName, setDeleteSpaceName] = useState("");
  const [showConfirmSaveSpaceModal, setShowConfirmSaveSpaceModal] = useState(false);

  // Encargados de espacio: colaboradores (sin rol admin) que administran uno
  // o varios espacios concretos. `encargados` es la lista plana que devuelve
  // el backend, una fila por persona y espacio.
  const [encargados, setEncargados] = useState([]);
  // Espacios marcados en "Editar usuario" para la persona que se está editando.
  const [espaciosDelEditado, setEspaciosDelEditado] = useState([]);

  const fetchUsers = async () => {
    const res = await authFetch(`${API}/api/users`);
    if (res.ok) setUsers(await res.json());
  };
  const fetchEncargados = async () => {
    const res = await authFetch(`${API}/api/spaces/managers`);
    if (res.ok) {
      const filas = await res.json();
      setEncargados(Array.isArray(filas) ? filas : []);
    }
  };
  const espaciosDe = (userId) =>
    encargados.filter((e) => e.userId === userId).map((e) => e.spaceId);
  const encargadosDe = (spaceId) => encargados.filter((e) => e.spaceId === spaceId);

  useEffect(() => {
    (async () => {
      try {
        await fetchUsers();
        const sRes = await authFetch(`${API}/api/spaces`);
        const { spaces: sJson = [] } = await sRes.json();
        setSpaces(sJson);
        await fetchEncargados();
        
        // Fetch posts
        const pRes = await authFetch(`${API}/api/posts`);
        if (pRes.ok) {
          const postsData = await pRes.json();
          setPosts(Array.isArray(postsData) ? postsData : postsData.posts || []);
        }
        
        // Fetch comments
        const cRes = await authFetch(`${API}/api/comments`);
        if (cRes.ok) {
          const commentsData = await cRes.json();
          setComments(Array.isArray(commentsData) ? commentsData : commentsData.comments || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const loadPublicationConfig = async () => {
      if (selectedTemplate !== "publication") return;
      const spaceNum = Number(selectedSpace);
      if (!spaceNum || spaceNum <= 0) return;

      try {
        const res = await authFetch(`${API}/api/email/publication-config?spaceId=${spaceNum}`);
        const data = await res.json();

        if (res.ok && data?.config) {
          setEmailConfig((prev) => ({
            ...prev,
            distributionType: data.config.distributionType || "space_users",
            publicationActive: Boolean(data.config.isActive)
          }));
          setSelectedUserIds(Array.isArray(data.config.selectedUserIds) ? data.config.selectedUserIds : []);
        }
      } catch (error) {
        // Error silencioso al cargar configuración
      }
    };

    loadPublicationConfig();
  }, [selectedSpace, selectedTemplate]);

  // Cerrar menú móvil cuando cambia de desktop a mobile
  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Cargar preferencia de espacio guardada para cada plantilla desde BD
  useEffect(() => {
    const loadTemplateSpacePreference = async () => {
      try {
        const res = await authFetch(
          `${API}/api/email/template-space-preference?userId=${currentUser?.id}&templateType=${selectedTemplate}`
        );
        const data = await res.json();

        if (!data.success) return;

        if (data.spaceId === null || data.spaceId === undefined) {
          setSelectedSpace("");
          return;
        }

        setSelectedSpace(String(data.spaceId));
      } catch (error) {
        // Error silencioso al cargar
      }
    };

    if (currentUser?.id && selectedTemplate) {
      loadTemplateSpacePreference();
    }
  }, [selectedTemplate, currentUser?.id]);

  // Guardar preferencia de espacio en BD cuando se selecciona
  const handleSelectSpace = async (spaceId) => {
    setSelectedSpace(spaceId);

    if (currentUser?.id && selectedTemplate) {
      try {
        await authFetch(`${API}/api/email/template-space-preference`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: currentUser.id,
            templateType: selectedTemplate,
            spaceId: spaceId === "" ? null : Number(spaceId)
          })
        });
      } catch (error) {
        // Error silencioso al guardar
      }
    }
  };

  const savePublicationConfig = async () => {
    // Convertir a número para comparación consistente
    const spaceToSave = Number(selectedSpace);

    if (!spaceToSave || spaceToSave <= 0) {
      setSuccessMessage({
        title: "Error",
        message: "Debes seleccionar un espacio antes de guardar la configuración.",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    if (emailConfig.distributionType === "specific_users" && selectedUserIds.length === 0) {
      setSuccessMessage({
        title: "Error",
        message: "Selecciona al menos un usuario para distribución específica",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    try {
      const res = await authFetch(`${API}/api/email/publication-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          spaceId: spaceToSave,
          isActive: Boolean(emailConfig.publicationActive),
          distributionType: emailConfig.distributionType,
          selectedUserIds,
          updatedBy: currentUser?.id || null
        })
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result?.message || "No fue posible guardar la configuración");
      }

      const selectedSpaceName = spaces.find(s => Number(s.id) === spaceToSave)?.name || "el espacio";

      setSuccessMessage({
        title: "Guardado correctamente",
        message: `La configuración de envío de correos para "${selectedSpaceName}" ha sido guardada correctamente en la base de datos.`,
        icon: "✓"
      });
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 4000);
    } catch (error) {
      setSuccessMessage({
        title: "Error",
        message: "Error: " + error.message,
        icon: "!"
      });
      setShowSuccessModal(true);
    }
  };

  const handleLogout = () => {
    logout?.() ?? localStorage.removeItem("token");
    navigate("/login");
  };

  // CLIENT-SIDE CSV
  const handleExport = () => {
    const fields = [
      "name",
      "username",
      "documento",
      "email",
      "phone",
      "country",
      "rol",
      "direccionLaboral",
    ];
    const rows = [fields.join(",")];
    users.forEach((u) => {
      const line = fields
        .map((f) => `"${(u[f] ?? "").toString().replace(/"/g, '""')}"`)
        .join(",");
      rows.push(line);
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usuarios_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    setSuccessMessage({
      title: "Exportación completada",
      message: "Los datos de usuarios fueron exportados correctamente en formato CSV.",
      icon: "✓"
    });
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 3200);
  };

  // IMPORT USERS
  const handleImportUsers = async () => {
    if (!importFile) {
      setSuccessMessage({
        title: "Error",
        message: "Por favor selecciona un archivo CSV o Excel",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    setImportLoading(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("updateExisting", importUpdateExisting.toString());
      formData.append("mode", importMode);

      const res = await authFetch(`${API}/api/users/import`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json();

      if (res.ok) {
        setImportResults(result);
        await fetchUsers(); // Refresh users list

        setSuccessMessage({
          title: "Importación completada",
          message: importMode === "pointsOnly"
            ? `Proceso completado. Se registraron ${result.successful || 0} movimientos de puntos.`
            : `Proceso completado. ${result.successful || 0} usuarios importados correctamente.`,
          icon: "✓"
        });
        setShowSuccessModal(true);
        setTimeout(() => setShowSuccessModal(false), 3200);
      } else {
        setSuccessMessage({
          title: "Error",
          message: result.error || "Error al importar usuarios",
          icon: "!"
        });
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Error importing users:", err);
      setSuccessMessage({
        title: "Error",
        message: "Error al procesar el archivo",
        icon: "!"
      });
      setShowSuccessModal(true);
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "csv" && ext !== "xlsx" && ext !== "xls") {
        setSuccessMessage({
          title: "Error",
          message: "Solo se permiten archivos CSV o Excel",
          icon: "!"
        });
        setShowSuccessModal(true);
        e.target.value = "";
        return;
      }
      setImportFile(file);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportUpdateExisting(false);
    setImportResults(null);
    setImportMode("full");
  };

  const downloadCSVTemplate = () => {
    const template = importMode === "pointsOnly"
      ? `Documento,Puntos (+/-),Motivo\n1234567890,100,Bono trimestral\n1234567890,-30,Canje de premio`
      : `Nombre,Apellido,Documento,Puntos,Correo,Fecha de nacimiento,Fecha de ingreso,Cargo,Area
Juan,Perez,1234567890,100,juan.perez@assaabloy.com,1990-05-15,2020-01-10,Especialista,RRHH,`;

    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = importMode === "pointsOnly"
      ? "plantilla_puntos_yale.csv"
      : "plantilla_importar_usuarios.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Plantilla con el saldo ACTUAL de cada usuario ya rellenado (Documento,
  // Nombre, Puntos actuales) más las columnas en blanco para el movimiento:
  // así el admin no tiene que ir a buscar el saldo de cada quien a mano
  // antes de armar la recarga.
  const downloadCurrentPointsTemplate = async () => {
    try {
      const res = await authFetch(`${API}/api/users/points/export-template`);
      if (!res.ok) throw new Error("No se pudo generar la plantilla");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_recarga_puntos_yale.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSuccessMessage({ title: "Error", message: err.message || "No se pudo descargar la plantilla", icon: "!" });
      setShowSuccessModal(true);
    }
  };

  // EXPORT METRICS
  const handleExportMetrics = () => {
    setShowExportModal(true);
  };

  const toggleMetric = (metric) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  const toggleAllMetrics = (value) => {
    setSelectedMetrics({
      generalMetrics: value,
      roleDistribution: value,
      spaceActivity: value,
      trafficData: value,
      areaDistribution: value,
      ageDistribution: value,
    });
  };

  const confirmExportMetrics = () => {
    const workbook = XLSX.utils.book_new();
    const now = new Date();
    
    // Hoja de Resumen
    const summaryData = [
      ["REPORTE DE MÉTRICAS - PLATAFORMA ASSA ABLOY"],
      ["Fecha de Exportación:", now.toLocaleDateString('es-ES')],
      []
    ];

    if (selectedMetrics.generalMetrics) {
      summaryData.push(["MÉTRICAS GENERALES"]);
      summaryData.push(["Total Usuarios", users.length]);
      summaryData.push(["Total Espacios", spaces.length]);
      summaryData.push(["Usuarios Activos", users.filter(u => u.estado === 'activo').length]);
      summaryData.push(["Usuarios - Últimas 24 horas", users.filter(u => {
        if (!u.ultimoLogin) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const lastLogin = aInstante(u.ultimoLogin);
        if (!lastLogin) return false;
        lastLogin.setHours(0, 0, 0, 0);
        return lastLogin.getTime() === today.getTime();
      }).length]);
      summaryData.push([]);
    }

    if (selectedMetrics.roleDistribution) {
      summaryData.push(["DISTRIBUCIÓN POR ROL"]);
      summaryData.push(["Administradores", users.filter(u => u.rol === 'admin').length]);
      summaryData.push(["Empleados", users.filter(u => u.rol === 'empleado').length]);
      summaryData.push(["Temporales", users.filter(u => u.rol === 'temporal').length]);
      summaryData.push([]);
    }

    if (selectedMetrics.spaceActivity) {
      summaryData.push(["ACTIVIDAD DE ESPACIOS"]);
      summaryData.push(["Total Posts", posts.length]);
      summaryData.push(["Total Comentarios", comments.length]);
      summaryData.push([]);
    }

    if (selectedMetrics.areaDistribution) {
      summaryData.push(["DISTRIBUCIÓN POR ÁREA"]);
      const areaCount = {};
      users.forEach(u => {
        const area = u.area || 'Sin área';
        areaCount[area] = (areaCount[area] || 0) + 1;
      });
      Object.entries(areaCount).forEach(([area, count]) => {
        summaryData.push([area, count]);
      });
      summaryData.push([]);
    }

    if (selectedMetrics.ageDistribution) {
      summaryData.push(["DISTRIBUCIÓN POR EDAD"]);
      const ageRanges = {
        '18-25': 0,
        '26-35': 0,
        '36-45': 0,
        '46-55': 0,
        '56+': 0
      };
      users.forEach(u => {
        if (u.age) {
          const age = parseInt(u.age);
          if (age <= 25) ageRanges['18-25']++;
          else if (age <= 35) ageRanges['26-35']++;
          else if (age <= 45) ageRanges['36-45']++;
          else if (age <= 55) ageRanges['46-55']++;
          else ageRanges['56+']++;
        }
      });
      Object.entries(ageRanges).forEach(([range, count]) => {
        summaryData.push([range, count]);
      });
    }

    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    ws['!cols'] = [{ wch: 30 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, ws, "Resumen");

    // Hoja de Tráfico
    if (selectedMetrics.trafficData) {
      const trafficData = [["Fecha", "Usuarios", "Posts"]];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        
        const usersCount = users.filter(u => {
          if (!u.ultimoLogin) return false;
          const loginDate = aInstante(u.ultimoLogin);
          if (!loginDate) return false;
          loginDate.setHours(0, 0, 0, 0);
          return loginDate.getTime() === date.getTime();
        }).length;

        const postsCount = posts.filter(p => {
          // El campo se llama `date`, no `createdAt`: con el nombre viejo
          // esta cuenta daba siempre 0 y la hoja de tráfico exportaba una
          // columna de ceros.
          const postDate = aInstante(p.date);
          if (!postDate) return false;
          postDate.setHours(0, 0, 0, 0);
          return postDate.getTime() === date.getTime();
        }).length;

        trafficData.push([date.toISOString().split('T')[0], usersCount, postsCount]);
      }
      const wsTraffic = XLSX.utils.aoa_to_sheet(trafficData);
      wsTraffic['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(workbook, wsTraffic, "Tráfico");
    }

    // Hoja de Espacios
    if (selectedMetrics.spaceActivity) {
      const spacesData = [["Espacio", "Posts", "Comentarios"]];
      spaces.forEach(space => {
        const spacePosts = posts.filter(p => p.spaceId === space.id).length;
        const spaceComments = comments.filter(c => {
          const post = posts.find(p => p.id === c.postId);
          return post && post.spaceId === space.id;
        }).length;
        spacesData.push([space.name, spacePosts, spaceComments]);
      });
      const wsSpaces = XLSX.utils.aoa_to_sheet(spacesData);
      wsSpaces['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, wsSpaces, "Espacios");
    }

    XLSX.writeFile(workbook, `metricas_${now.toISOString().slice(0, 10)}.xlsx`);
    setShowExportModal(false);

    setSuccessMessage({
      title: "Exportación completada",
      message: "Las métricas fueron exportadas correctamente en formato Excel.",
      icon: "✓"
    });
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 3200);
  };

  // DELETE USER
  const openDeleteModal = (id, name) => {
    setDeleteUserId(id);
    setDeleteUserName(name);
    setShowDeleteModal(true);
  };
  const confirmDeleteUser = async () => {
    if (!deleteUserId) return;
    await authFetch(`${API}/api/users/${deleteUserId}`, { method: "DELETE" });
    setUsers((u) => u.filter((x) => x.id !== deleteUserId));
    setEncargados((lista) => lista.filter((e) => e.userId !== deleteUserId));
    setShowDeleteModal(false);
    setDeleteUserId(null);
  };

  // EDIT USER
  const openEditModal = (user) => {
    const formatDateForInput = (dateStr) => {
      if (!dateStr) return '';
      // Si es ISO format, extrae YYYY-MM-DD
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      // Si ya está en formato correcto
      if (dateStr.length === 10) return dateStr;
      return '';
    };
    
    setEditingUser(user);
    setEspaciosDelEditado(espaciosDe(user.id));
    setEditUserData({
      ...user,
      fechaIngreso: formatDateForInput(user.fechaIngreso),
      fechaNacimiento: formatDateForInput(user.fechaNacimiento)
    });
    setShowEditModal(true);
  };
  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditUserData((d) => ({ ...d, [name]: value }));
  };
  const saveEditedUser = async () => {
    if (!editingUser?.id) {
      setSuccessMessage({
        title: "Error",
        message: "No se encontró el usuario a editar.",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    const payload = {
      ...editUserData,
      name: (editUserData.name || "").trim(),
      username: (editUserData.username || "").trim(),
      email: (editUserData.email || "").trim(),
      fechaIngreso: editUserData.fechaIngreso || null,
      fechaNacimiento: editUserData.fechaNacimiento || null,
      age: editUserData.age === "" ? null : Number(editUserData.age),
      points: editUserData.points === "" ? 0 : Number(editUserData.points),
    };

    try {
      const res = await authFetch(`${API}/api/users/${editingUser.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body?.error || body?.message || "Error al actualizar usuario");
      }

      const updatedUser = body?.id ? body : body?.user;

      if (updatedUser?.id) {
        setUsers((u) => u.map((x) => (x.id === updatedUser.id ? updatedUser : x)));
      } else {
        await fetchUsers();
      }

      // Los espacios que administra se guardan aparte, y solo si cambiaron.
      // A un administrador no se le toca nada: ya gestiona todos.
      const antes = espaciosDe(editingUser.id).slice().sort().join(",");
      const ahora = espaciosDelEditado.slice().sort().join(",");
      if (payload.rol !== "admin" && antes !== ahora) {
        const resEspacios = await authFetch(`${API}/api/spaces/managers/${editingUser.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ spaceIds: espaciosDelEditado }),
        });
        if (!resEspacios.ok) {
          throw new Error(
            await mensajeDeError(resEspacios, "Los datos se guardaron, pero no los espacios que administra")
          );
        }
        await fetchEncargados();
      }

      setShowEditModal(false);
      setEditUserData({});

      setSuccessMessage({
        title: "Usuario actualizado",
        message: `Los datos de ${(updatedUser?.name || payload.name || "el usuario")} han sido actualizados correctamente.`,
        icon: "OK"
      });
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3500);
    } catch (error) {
      setSuccessMessage({
        title: "Error",
        message: error.message || "Error al actualizar usuario",
        icon: "!"
      });
      setShowSuccessModal(true);
    }
  };

  const toggleUserEstado = async (user) => {
    const nextEstado = (user.estado || "activo") === "activo" ? "inactivo" : "activo";
    setUpdatingUserId(user.id);

    try {
      const res = await authFetch(`${API}/api/users/${user.id}/estado`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado: nextEstado }),
      });

      const responseText = await res.text();
      let responseData = null;

      if (responseText) {
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          responseData = { raw: responseText };
        }
      }

      if (!res.ok) {
        const fallbackMessage = responseText && responseText.trim().startsWith("<")
          ? "El servidor devolvió una respuesta inesperada al actualizar el estado"
          : "No fue posible actualizar el estado";
        throw new Error(responseData?.error || responseData?.message || fallbackMessage);
      }

      const updated = responseData;
      if (!updated?.id) {
        throw new Error("La respuesta del servidor no contiene el usuario actualizado");
      }

      setUsers((currentUsers) =>
        currentUsers.map((currentUser) =>
          currentUser.id === updated.id ? updated : currentUser
        )
      );

      setSuccessMessage({
        title: nextEstado === "activo" ? "Usuario activado" : "Usuario inactivado",
        message: `${updated.name} ahora está ${nextEstado === "activo" ? "activo" : "inactivo"}.`,
        icon: "OK"
      });
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3200);
    } catch (error) {
      setSuccessMessage({
        title: "Error",
        message: "Error al actualizar estado: " + error.message,
        icon: "!"
      });
      setShowSuccessModal(true);
    } finally {
      setUpdatingUserId(null);
    }
  };

  // DELETE USER (old)
  const handleDeleteUser = async (id) => {
    await authFetch(`${API}/api/users/${id}`, { method: "DELETE" });
    setUsers((u) => u.filter((x) => x.id !== id));
  };

  // CREATE USER
  const handleNewUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUser((n) => ({
      ...n,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const handleCreateUser = async () => {
    setCreateError("");
    const { sendWelcomeEmail, ...userDataForAPI } = newUser;
    
    try {
      const res = await authFetch(`${API}/api/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userDataForAPI),
      });
      const body = await res.json();
      if (res.ok) {
        setUsers((u) => [...u, body]);
        
        // Enviar correo de bienvenida si está seleccionado
        let emailSent = false;
        if (sendWelcomeEmail && body.email) {
          try {
            // Obtener la plantilla de bienvenida
            const welcomeTemplate = emailTemplates.welcome;
            
            await emailService.sendWelcomeEmail({
              user_id: body.id,
              email: body.email,
              nombre: body.name,
              usuario: body.username,
              password: body.documento, // La contraseña inicial es el documento
              template: welcomeTemplate // Enviar la plantilla
            });
            emailSent = true;
          } catch (emailErr) {
            console.error("Error enviando correo:", emailErr);
          }
        }
        
        setShowCreateModal(false);
        setShowOptionalFields(false);
        
        // Mostrar mensaje de éxito personalizado
        setSuccessMessage({
          title: "Usuario creado",
          message: emailSent 
            ? `El usuario ${body.name} ha sido creado correctamente y se ha enviado un correo de bienvenida a ${body.email} con sus credenciales de acceso.`
            : `El usuario ${body.name} ha sido creado correctamente.`,
          icon: "OK"
        });
        setShowSuccessModal(true);
        
        // Resetear formulario
        setNewUser({
          name: "",
          username: "",
          documento: "",
          email: "",
          phone: "",
          country: "",
          rol: "empleado",
          direccionLaboral: "",
          linkedIn: "",
          fechaIngreso: "",
          fechaNacimiento: "",
          sendWelcomeEmail: true,
        });
        
        setTimeout(() => setShowSuccessModal(false), 5000);
      } else {
        setCreateError(body.error || "Error desconocido al crear usuario");
      }
    } catch (err) {
      setCreateError("Error en la solicitud: " + err.message);
    }
  };

  // ENVIAR CORREOS DE CUMPLEAÑOS O ANIVERSARIO
  // El envío manual de cumpleaños y aniversario se retiró: esos dos correos
  // los manda solo el proceso diario, con lo guardado en la configuración de
  // celebraciones. El backend también rechaza /send-bulk para esas dos
  // plantillas, para que no se pueda disparar desde fuera de la interfaz.

  // RESET PASSWORD
  const openResetModal = (user) => {
    setResetTargetUser(user);
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    if (!resetTargetUser?.id || resettingPassword) return;
    setResettingPassword(true);

    try {
      const res = await authFetch(`${API}/api/users/${resetTargetUser.id}/reset-password`, {
        method: "POST",
      });

      if (!res.ok) {
        throw new Error("No se pudo restablecer la contraseña");
      }

      setShowResetModal(false);
      setShowOperationModal(true);
      setOperationModalData({
        title: "Contraseña restablecida",
        message: "La contraseña ya fue cambiada a su documento de identidad.",
        isError: false,
      });
      await fetchUsers();
    } catch (error) {
      setShowOperationModal(true);
      setOperationModalData({
        title: "Error",
        message: error.message || "Error al resetear contraseña",
        isError: true,
      });
    } finally {
      setResettingPassword(false);
      setResetTargetUser(null);
    }
  };

  // CHANGE PASSWORD MANUAL
  const openPwdModal = (id) => {
    setPwdUserId(id);
    setNewPassword("");
    setConfirmPassword("");
    setShowPwdModal(true);
  };

  const changePassword = async () => {
    if (passwordChanging) return;

    const passwordPolicy = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordPolicy.test(newPassword)) {
      setSuccessMessage({
        title: "Error",
        message: "La contraseña debe tener al menos 8 caracteres, con mayúscula, minúscula y número.",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setSuccessMessage({
        title: "Error",
        message: "La confirmación de contraseña no coincide.",
        icon: "!"
      });
      setShowSuccessModal(true);
      return;
    }

    setPasswordChanging(true);
    try {
      const res = await authFetch(`${API}/api/users/${pwdUserId}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      });

      if (res.ok) {
        setShowPwdModal(false);
        setShowOperationModal(true);
        setOperationModalData({
          title: "Contraseña actualizada",
          message: "La contraseña del usuario se actualizó correctamente.",
          isError: false,
        });
        await fetchUsers();
      } else {
        setShowOperationModal(true);
        setOperationModalData({
          title: "Error",
          message: "Error al cambiar contraseña",
          isError: true,
        });
      }
    } catch (error) {
      setShowOperationModal(true);
      setOperationModalData({
        title: "Error",
        message: error.message || "Error al cambiar contraseña",
        isError: true,
      });
    } finally {
      setPasswordChanging(false);
    }
  };

  const openCreateSpaceModal = () => {
    setEditingSpaceId(null);
    setSpaceForm({ name: "", description: "", imageFile: null, imagePreview: "" });
    setShowSpaceModal(true);
  };
  const openEditSpaceModal = (space) => {
    setEditingSpaceId(space.id);
    setSpaceForm({
      name: space.name,
      description: space.description,
      imageFile: null,
      imagePreview: space.image || ""
    });
    setShowSpaceModal(true);
  };
  const handleSpaceFormChange = (e) => {
    const { name, value } = e.target;
    setSpaceForm((prev) => ({ ...prev, [name]: value }));
  };
    const handleSaveSpace = async () => {
    const method = editingSpaceId ? "PUT" : "POST";
    const url = editingSpaceId
      ? `${API}/api/spaces/${editingSpaceId}`
      : `${API}/api/spaces`;

    const formData = new FormData();
    formData.append("name", spaceForm.name);
    formData.append("description", spaceForm.description);
    formData.append("userId", currentUser.id);
    if (spaceForm.imageFile) {
      formData.append("image", spaceForm.imageFile);
    }

    const res = await authFetch(url, {
      method,
      body: formData,
    });

    if (res.ok) {
      const updated = await res.json();
      if (editingSpaceId) {
        setSpaces((s) => s.map((x) => (x.id === updated.id ? updated : x)));
      } else {
        setSpaces((s) => [...s, updated]);
      }
      setShowSpaceModal(false);
    } else {
      setSuccessMessage({
        title: "Error",
        message: "Error al guardar espacio",
        icon: "!"
      });
      setShowSuccessModal(true);
    }
  };

  // Wrapper to be called when the user confirms the save in the confirmation modal
  const handleConfirmSaveSpace = async () => {
    setShowConfirmSaveSpaceModal(false);
    await handleSaveSpaceConfirmed();
  };

  // keep original save logic in a named function so it can be invoked after confirmation
  async function handleSaveSpaceConfirmed() {
    const method = editingSpaceId ? "PUT" : "POST";
    const url = editingSpaceId
      ? `${API}/api/spaces/${editingSpaceId}`
      : `${API}/api/spaces`;

    const formData = new FormData();
    formData.append("name", spaceForm.name);
    formData.append("description", spaceForm.description);
    formData.append("userId", currentUser.id);
    if (spaceForm.imageFile) {
      formData.append("image", spaceForm.imageFile);
    }

    const res = await authFetch(url, {
      method,
      body: formData,
    });

    if (res.ok) {
      const updated = await res.json();
      if (editingSpaceId) {
        setSpaces((s) => s.map((x) => (x.id === updated.id ? updated : x)));
      } else {
        setSpaces((s) => [...s, updated]);
      }
      setShowSpaceModal(false);
    } else {
      setSuccessMessage({
        title: "Error",
        message: "Error al guardar espacio",
        icon: "!"
      });
      setShowSuccessModal(true);
    }
  }

  const openDeleteSpaceModal = (id, name) => {
    setDeleteSpaceId(id);
    setDeleteSpaceName(name || "");
    setShowDeleteSpaceModal(true);
  };
  const confirmDeleteSpace = async () => {
    if (!deleteSpaceId) return;
    const res = await authFetch(`${API}/api/spaces/${deleteSpaceId}`, { method: "DELETE" });
    if (res.ok) {
      setSpaces((s) => s.filter((x) => x.id !== deleteSpaceId));
      setEncargados((lista) => lista.filter((e) => e.spaceId !== deleteSpaceId));
    } else {
      setSuccessMessage({ title: "Error", message: "No fue posible eliminar el espacio", icon: "!" });
      setShowSuccessModal(true);
    }
    setShowDeleteSpaceModal(false);
    setDeleteSpaceId(null);
    setDeleteSpaceName("");
  };

  // Removed blocking loading screen per user request: render UI immediately

  return (
    <div className="admin-container">
      {/* Overlay oscuro cuando sidebar está abierto en mobile */}
      {isMobile && mobileMenuOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Hamburger Menu para Mobile */}
      {isMobile && (
        <button 
          className="hamburger-menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Abrir menú"
        >
          <MenuIcon fontSize="large" />
        </button>
      )}

      <aside className={`admin-sidebar ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">AA</div>
            <div className="logo-text">
              <div className="brand-name">ASSA ABLOY</div>
              <div className="panel-name">Panel Admin</div>
            </div>
          </div>
          {isMobile && (
            <button 
              className="close-sidebar"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Close />
            </button>
          )}
        </div>
        
        <nav className="sidebar-nav">
          <button
            className={view === "dashboard" ? "active" : ""}
            onClick={() => {
              setView("dashboard");
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            <div className="nav-icon"><TrendingUp fontSize="small" /></div>
            <div className="nav-content">
              <span className="nav-title">Dashboard</span>
              <span className="nav-subtitle">Vista general</span>
            </div>
          </button>
          <button
            className={view === "users" ? "active" : ""}
            onClick={() => {
              setView("users");
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            <div className="nav-icon"><People fontSize="small" /></div>
            <div className="nav-content">
              <span className="nav-title">Usuarios</span>
              <span className="nav-subtitle">{users.length} registrados</span>
            </div>
          </button>
          <button
            className={view === "spaces" ? "active" : ""}
            onClick={() => {
              setView("spaces");
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            <div className="nav-icon"><Storage fontSize="small" /></div>
            <div className="nav-content">
              <span className="nav-title">Espacios</span>
              <span className="nav-subtitle">{spaces.length} comunidades</span>
            </div>
          </button>
          <button
            className={view === "notifications" ? "active" : ""}
            onClick={() => {
              setView("notifications");
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            <div className="nav-icon"><Email fontSize="small" /></div>
            <div className="nav-content">
              <span className="nav-title">Notificaciones</span>
              <span className="nav-subtitle">Correos y alertas</span>
            </div>
          </button>
          {/* Registros: pantalla de solo consulta (correos enviados y
              movimientos de puntos). Va al final del menú porque no se
              administra nada desde ahí, solo se mira. */}
          <button
            className={view === "logs" ? "active" : ""}
            onClick={() => {
              setView("logs");
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            <div className="nav-icon"><HistoryRoundedIcon fontSize="small" /></div>
            <div className="nav-content">
              <span className="nav-title">Registros del sistema</span>
              <span className="nav-subtitle">Auditoría y trazabilidad</span>
            </div>
          </button>
        </nav>
      </aside>

      <div className="contentWrapper">
        <header className="admin-header">
          <h1 className="page-title">{isMobile ? "Admin" : "Panel de Administración"}</h1>
          <div className="admin-actions">
            {!isMobile && (
              <button className="btn-back" onClick={() => navigate(-1)}>
                <span className="btn-text">Volver</span>
              </button>
            )}
            <button className="btn-logout" onClick={handleLogout}>
              <span className="btn-text">{isMobile ? "Salir" : "Cerrar sesión"}</span>
            </button>
          </div>
        </header>

        {view === "dashboard" && (
          <>
            <section className="admin-card">
              <div className="dashboard-toolbar">
                <p><strong>Bienvenido al dashboard</strong></p>
                <div className="dashboard-toolbar__acciones">
                  <button
                    className="exportBtn metricsExportBtn"
                    onClick={handleExportMetrics}
                    title="Exportar métricas"
                  >
                    <Download fontSize="small" /> Exportar Métricas
                  </button>
                  <div className="view-layout-controls">
                    <button
                      className={`layout-btn ${dashboardViewLayout === "widgets" ? "active" : ""}`}
                      onClick={() => setDashboardViewLayout("widgets")}
                      title="Vista de widgets"
                    >
                      <ViewModule fontSize="small" />
                    </button>
                    <button
                      className={`layout-btn ${dashboardViewLayout === "list" ? "active" : ""}`}
                      onClick={() => setDashboardViewLayout("list")}
                      title="Vista de lista"
                    >
                      <ViewList fontSize="small" />
                    </button>
                    <button
                      className={`layout-btn ${dashboardViewLayout === "compact" ? "active" : ""}`}
                      onClick={() => setDashboardViewLayout("compact")}
                      title="Vista compacta"
                    >
                      <ViewComfy fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* TARJETAS DE MÉTRICAS */}
            <section className="metrics-cards">
              <div className="metric-card primary">
                <div className="metric-icon">
                  <People />
                </div>
                <div className="metric-content">
                  <h4>Total Usuarios</h4>
                  <p className="metric-value">{users.length}</p>
                  <span className="metric-subtitle">Activos en el sistema</span>
                </div>
              </div>

              <div className="metric-card secondary">
                <div className="metric-icon">
                  <Storage />
                </div>
                <div className="metric-content">
                  <h4>Total Espacios</h4>
                  <p className="metric-value">{spaces.length}</p>
                  <span className="metric-subtitle">Comunidades creadas</span>
                </div>
              </div>

              <div className="metric-card accent">
                <div className="metric-icon">
                  <TrendingUp />
                </div>
                <div className="metric-content">
                  <h4>Usuarios Activos</h4>
                  <p className="metric-value">{users.filter(u => u.estado === 'activo').length}</p>
                  <span className="metric-subtitle">Usuarios en linea</span>
                </div>
              </div>

              <div className="metric-card success">
                <div className="metric-icon">
                  <People />
                </div>
                <div className="metric-content">
                  <h4>Usando Plataforma Hoy</h4>
                  <p className="metric-value">
                    {users.filter(u => {
                      if (!u.ultimoLogin) return false;
                      const lastLogin = aInstante(u.ultimoLogin);
                      if (!lastLogin) return false;
                      // Ventana real de 24 horas (no "mismo día calendario"):
                      // así el número coincide de verdad con la etiqueta de
                      // abajo, sin resetear a 0 apenas pasa la medianoche.
                      return Date.now() - lastLogin.getTime() <= 24 * 60 * 60 * 1000;
                    }).length}
                  </p>
                  <span className="metric-subtitle">Últimas 24 horas</span>
                </div>
              </div>
            </section>

            {/* GRÁFICAS */}
            <section className={`charts-grid charts-${dashboardViewLayout}`}>
              {/* GRÁFICA DE USUARIOS POR ROL */}
              <div className="chart-container">
                <h3>Distribución por Rol</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "Admin",
                          value: users.filter((u) => u.rol === "admin").length,
                        },
                        {
                          name: "Empleado",
                          value: users.filter((u) => u.rol === "empleado")
                            .length,
                        },
                        {
                          name: "Temporal",
                          value: users.filter((u) => u.rol === "temporal")
                            .length,
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#005fa3" />
                      <Cell fill="#0077c8" />
                      <Cell fill="#00a0d0" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE TRÁFICO */}
              <div className="chart-container">
                <h3>Tráfico de Plataforma</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={[
                      { 
                        day: "Día 1", 
                        posts: Math.ceil(posts.length / 7), 
                        usuarios: Math.ceil(users.length / 7) 
                      },
                      { 
                        day: "Día 2", 
                        posts: Math.ceil(posts.length / 6), 
                        usuarios: Math.ceil(users.length / 6) 
                      },
                      { 
                        day: "Día 3", 
                        posts: Math.ceil(posts.length / 5), 
                        usuarios: Math.ceil(users.length / 5) 
                      },
                      { 
                        day: "Día 4", 
                        posts: Math.ceil(posts.length / 4), 
                        usuarios: Math.ceil(users.length / 4) 
                      },
                      { 
                        day: "Día 5", 
                        posts: Math.ceil(posts.length / 3), 
                        usuarios: Math.ceil(users.length / 3) 
                      },
                      { 
                        day: "Día 6", 
                        posts: Math.ceil(posts.length / 2), 
                        usuarios: Math.ceil(users.length / 2) 
                      },
                      { 
                        day: "Hoy", 
                        posts: posts.length, 
                        usuarios: users.length 
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="day" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="posts"
                      stroke="#0077c8"
                      strokeWidth={2}
                      dot={{ fill: "#0077c8", r: 5 }}
                      activeDot={{ r: 7 }}
                      name="Posts"
                    />
                    <Line
                      type="monotone"
                      dataKey="usuarios"
                      stroke="#00a0d0"
                      strokeWidth={2}
                      dot={{ fill: "#00a0d0", r: 5 }}
                      activeDot={{ r: 7 }}
                      name="Usuarios"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE ESPACIOS */}
              <div className="chart-container">
                <h3>Actividad de Espacios</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={spaces.map((s) => ({
                      name: s.name?.substring(0, 12) || `E${s.id}`,
                      posts: posts.filter(p => p.spaceId === s.id).length,
                      comentarios: posts
                        .filter(p => p.spaceId === s.id)
                        .reduce((sum, p) => sum + (p.commentsCount || 0), 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="posts" fill="#0077c8" radius={[8, 8, 0, 0]} />
                    <Bar
                      dataKey="comentarios"
                      fill="#00a0d0"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE USO */}
              <div className="chart-container">
                <h3>Uso de Plataforma</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { actividad: "Usuarios", valor: users.length },
                      { actividad: "Posts", valor: posts.length },
                      { actividad: "Espacios", valor: spaces.length },
                      { actividad: "Comentarios", valor: comments.length },
                      { 
                        actividad: "Likes", 
                        valor: posts.reduce((sum, p) => sum + (p.likes || 0), 0) 
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="actividad" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Bar
                      dataKey="valor"
                      fill="#005fa3"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* CUMPLEAÑOS PRÓXIMOS - CARRUSEL */}
              <div className="birthday-carousel-container">
                <h3>Cumpleaños Próximos</h3>
                {(() => {
                  const today = new Date();
                  const nextMonth = new Date();
                  nextMonth.setDate(nextMonth.getDate() + 30);
                  
                  const upcomingBirthdays = users
                    .filter(u => {
                      if (!u.fechaNacimiento) return false;
                      // Asegurar que tenemos una fecha válida en formato YYYY-MM-DD
                      const dateStr = u.fechaNacimiento.split('T')[0]; // Tomar solo la parte de la fecha
                      const [year, month, day] = dateStr.split('-');
                      if (!year || !month || !day) return false;
                      
                      const userBirthday = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
                      if (userBirthday < today) {
                        userBirthday.setFullYear(today.getFullYear() + 1);
                      }
                      return userBirthday <= nextMonth && userBirthday >= today;
                    })
                    .sort((a, b) => {
                      const getNextBirthday = (fechaNacimiento) => {
                        const dateStr = fechaNacimiento.split('T')[0];
                        const [year, month, day] = dateStr.split('-');
                        const bd = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
                        if (bd < today) bd.setFullYear(today.getFullYear() + 1);
                        return bd;
                      };
                      return getNextBirthday(a.fechaNacimiento) - getNextBirthday(b.fechaNacimiento);
                    });

                  if (upcomingBirthdays.length === 0) {
                    return (
                      <div className="birthday-carousel">
                        <div className="empty-birthdays">
                          <p>No hay cumpleaños próximos en los próximos 30 días</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="birthday-carousel">
                      {upcomingBirthdays.map((u, idx) => {
                        const dateStr = u.fechaNacimiento.split('T')[0];
                        const [year, month, day] = dateStr.split('-');
                        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                        const monthStr = monthNames[parseInt(month) - 1];
                        
                        return (
                          <div key={u.id} className={`carousel-slide ${idx === 0 ? 'active' : ''}`}>
                            <div className="birthday-card-large">
                              <div className="birthday-date-large">
                                <span className="day-large">{day}</span>
                                <span className="month-large">{monthStr}</span>
                              </div>
                              
                              <div className="birthday-profile-large">
                                <div className="profile-image-large">
                                  {u.profilePicture ? (
                                    <img 
                                      src={urlDeArchivo(u.profilePicture)} 
                                      alt={u.name}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className="avatar-placeholder-large"
                                    style={{
                                      display: u.profilePicture ? 'none' : 'flex',
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}
                                  >
                                    {u.name?.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                
                                <div className="profile-info-large">
                                  <h4>{u.name}</h4>
                                  <p className="cargo">{u.cargo || 'Sin asignar'}</p>
                                  <p className="area">{u.area || 'Sin área'}</p>
                                  
                                  <div className="contact-info">
                                    <div className="contact-item">
                                      <span className="label">Email</span>
                                      <span className="value">{u.email}</span>
                                    </div>
                                    {u.phone && (
                                      <div className="contact-item">
                                        <span className="label">Tel</span>
                                        <span className="value">{u.phone}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="birthday-badge-large">Próximo</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="chart-container">
                <h3>Distribución por Área</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={(() => {
                      const areaGroups = {};
                      users.forEach(u => {
                        const area = u.area || 'Sin asignar';
                        areaGroups[area] = (areaGroups[area] || 0) + 1;
                      });
                      return Object.entries(areaGroups)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 8)
                        .map(([area, count]) => ({
                          name: area.substring(0, 15),
                          empleados: count,
                        }));
                    })()}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Bar dataKey="empleados" fill="#0077c8" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE DISTRIBUCIÓN POR PAÍS */}
              <div className="chart-container">
                <h3>Distribución Geográfica</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={(() => {
                      const countryGroups = {};
                      users.forEach(u => {
                        const country = u.country || 'Sin asignar';
                        countryGroups[country] = (countryGroups[country] || 0) + 1;
                      });
                      return Object.entries(countryGroups)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 8)
                        .map(([country, count]) => ({
                          pais: country.substring(0, 12),
                          empleados: count,
                        }));
                    })()}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="pais" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Bar dataKey="empleados" fill="#00a0d0" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE ANTIGÜEDAD */}
              <div className="chart-container">
                <h3>Antigüedad del Personal</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={(() => {
                      const today = new Date();
                      const antiguedad = {
                        'Menos de 1 año': 0,
                        '1-2 años': 0,
                        '2-3 años': 0,
                        '3-5 años': 0,
                        'Más de 5 años': 0,
                      };
                      users.forEach(u => {
                        if (!u.fechaIngreso) return;
                        const ingreso = new Date(u.fechaIngreso);
                        const diffTime = Math.abs(today - ingreso);
                        const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365);
                        
                        if (diffYears < 1) antiguedad['Menos de 1 año']++;
                        else if (diffYears < 2) antiguedad['1-2 años']++;
                        else if (diffYears < 3) antiguedad['2-3 años']++;
                        else if (diffYears < 5) antiguedad['3-5 años']++;
                        else antiguedad['Más de 5 años']++;
                      });
                      return Object.entries(antiguedad).map(([periodo, count]) => ({
                        periodo,
                        empleados: count,
                      }));
                    })()}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="periodo" stroke="#666" angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Bar dataKey="empleados" fill="#f39c12" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* GRÁFICA DE RANGO DE EDADES */}
              <div className="chart-container">
                <h3>Distribución por Edad</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={(() => {
                      const edadRangos = {
                        '18-25': 0,
                        '26-35': 0,
                        '36-45': 0,
                        '46-55': 0,
                        '56+': 0,
                      };
                      users.forEach(u => {
                        if (!u.age) return;
                        if (u.age < 26) edadRangos['18-25']++;
                        else if (u.age < 36) edadRangos['26-35']++;
                        else if (u.age < 46) edadRangos['36-45']++;
                        else if (u.age < 56) edadRangos['46-55']++;
                        else edadRangos['56+']++;
                      });
                      return Object.entries(edadRangos).map(([rango, count]) => ({
                        rango,
                        empleados: count,
                      }));
                    })()}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="rango" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <Bar dataKey="empleados" fill="#3498db" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </>
        )}

        {view === "users" && (
          <section className="users-section">
            <div className="users-header">
              <h3>Gestión de Usuarios</h3>
              <div className="users-header-actions">
                <button
                  className="createUserBtn"
                  onClick={() => {
                    setCreateError("");
                    setShowCreateModal(true);
                  }}
                >
                  Nuevo Usuario
                </button>
                <button
                  className="importUserBtn"
                  onClick={() => {
                    setImportMode("full");
                    setShowImportModal(true);
                  }}
                  title="Crear o actualizar colaboradores desde un archivo"
                >
                  <Upload fontSize="small" /> Importar Usuarios
                </button>
                {/* Cargar puntos es la tarea periódica del programa Yale y no
                    tiene nada que ver con dar de alta gente: merece su propio
                    botón en vez de estar escondida dentro de "Importar
                    Usuarios". Abre el mismo modal ya en modo puntos. */}
                <button
                  className="importPointsBtn"
                  onClick={() => {
                    setImportMode("pointsOnly");
                    setShowImportModal(true);
                  }}
                  title="Cargar o descontar puntos Yale desde un archivo"
                >
                  <Stars fontSize="small" /> Cargar Puntos
                </button>
                <button
                  className="exportBtn"
                  onClick={handleExport}
                  title="Descargar todos los colaboradores en CSV"
                >
                  <Download fontSize="small" /> Exportar CSV
                </button>
              </div>
            </div>

            <div className="search-export-row">
              <div className="usersSearch">
                <Search className="searchIcon" />
                <input
                  type="text"
                  value={searchTerm}
                  placeholder="Buscar por documento, nombre o usuario..."
                  onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                />
                {searchTerm && (
                  <Close
                    className="clearIcon"
                    onClick={() => setSearchTerm("")}
                  />
                )}
              </div>

              <div className="view-layout-controls">
                <button
                  className={`layout-btn ${usersViewLayout === "cards" ? "active" : ""}`}
                  onClick={() => setUsersViewLayout("cards")}
                  title="Vista de tarjetas"
                >
                  <ViewModule fontSize="small" />
                </button>
                <button
                  className={`layout-btn ${usersViewLayout === "list" ? "active" : ""}`}
                  onClick={() => setUsersViewLayout("list")}
                  title="Vista de lista"
                >
                  <ViewList fontSize="small" />
                </button>
                <button
                  className={`layout-btn ${usersViewLayout === "grid" ? "active" : ""}`}
                  onClick={() => setUsersViewLayout("grid")}
                  title="Vista compacta"
                >
                  <ViewComfy fontSize="small" />
                </button>
              </div>
            </div>

            <div className={`users-container users-${usersViewLayout}`}>
              {(() => {
                const filtered = users.filter(
                  (u) =>
                    u.name?.toLowerCase().includes(searchTerm) ||
                    u.username?.toLowerCase().includes(searchTerm) ||
                    u.documento?.toString().includes(searchTerm) ||
                    u.email?.toLowerCase().includes(searchTerm)
                );
                
                // Si hay búsqueda, mostrar todos. Si no, paginar
                const usersToShow = searchTerm ? filtered : filtered.slice(0, (usersPage + 1) * 9);
                
                return usersToShow.map((u) => (
                  <div key={u.id} className={`user-card ${u.estado !== "activo" ? "inactive" : ""}`}>
                    <div className="user-header">
                      <div className="user-avatar">
                        {u.profilePicture ? (
                          <img
                            src={urlDeArchivo(u.profilePicture)}
                            alt={u.name}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className="avatar-placeholder"
                          style={{
                            display: u.profilePicture ? "none" : "flex",
                          }}
                        >
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      {/* Solo el administrador lleva distintivo de rol: a
                          todo el mundo se le presupone colaborador, así que
                          etiquetar a cada ficha con "empleado" era ruido. */}
                      {u.rol === "admin" && <div className="user-badge">Admin</div>}
                      <div className={`user-status-badge ${u.estado === "activo" ? "active" : "inactive"}`}>
                        {u.estado === "activo" ? "Activo" : "Inactivo"}
                      </div>
                    </div>

                    <div className="user-info">
                      <h4>{u.name}</h4>
                      <p className="username">@{u.username}</p>
                      <div className="user-details">
                        <span className="detail">
                          <strong>Documento:</strong> {u.documento}
                        </span>
                        <span className="detail">
                          <strong>Email:</strong> {u.email}
                        </span>
                        {/* Talento Humano necesita ver el cumpleaños de un
                            vistazo desde la ficha, sin abrir el editor. */}
                        {formatearFechaCorta(u.fechaNacimiento) && (
                          <span className="detail">
                            <strong>Nacimiento:</strong> {formatearFechaCorta(u.fechaNacimiento)}
                          </span>
                        )}
                        {u.points !== undefined && (
                          <span className="detail points-detail">
                            <strong>Puntos:</strong> {u.points || 0}
                          </span>
                        )}
                        {u.phone && (
                          <span className="detail">
                            <strong>Teléfono:</strong> {u.phone}
                          </span>
                        )}
                        {u.country && (
                          <span className="detail">
                            <strong>País:</strong> {u.country}
                          </span>
                        )}
                        {u.direccionLaboral && (
                          <span className="detail">
                            <strong>Dirección:</strong> {u.direccionLaboral}
                          </span>
                        )}
                        {u.rol !== "admin" && espaciosDe(u.id).length > 0 && (
                          <span className="detail">
                            <strong>Encargado de:</strong>{" "}
                            {spaces
                              .filter((s) => espaciosDe(u.id).includes(s.id))
                              .map((s) => s.name)
                              .join(", ")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="user-actions">
                      <button
                        className={`actionBtn statusBtn ${u.estado === "activo" ? "deactivate" : "activate"}`}
                        onClick={() => toggleUserEstado(u)}
                        title={u.estado === "activo" ? "Inactivar usuario" : "Activar usuario"}
                        disabled={updatingUserId === u.id}
                      >
                        {u.estado === "activo" ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                      </button>
                      <button
                        className="actionBtn infoBtn"
                        onClick={() => openEditModal(u)}
                        title="Editar"
                      >
                        <Edit fontSize="small" />
                      </button>
                      <button
                        className="actionBtn resetBtn"
                        onClick={() => openResetModal(u)}
                        title="Resetear contraseña"
                      >
                        <LockReset fontSize="small" />
                      </button>
                      <button
                        className="actionBtn changePwdBtn"
                        onClick={() => openPwdModal(u.id)}
                        title="Cambiar contraseña"
                      >
                        <Lock fontSize="small" />
                      </button>
                      <button
                        className="actionBtn deleteBtn"
                        onClick={() => openDeleteModal(u.id, u.name)}
                        title="Eliminar"
                      >
                        <Delete fontSize="small" />
                      </button>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {(() => {
              const filtered = users.filter(
                (u) =>
                  u.name?.toLowerCase().includes(searchTerm) ||
                  u.username?.toLowerCase().includes(searchTerm) ||
                  u.documento?.toString().includes(searchTerm) ||
                  u.email?.toLowerCase().includes(searchTerm)
              );
              const totalPages = Math.ceil(filtered.length / 9);
              return !searchTerm && usersPage + 1 < totalPages ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <button
                    className="verMasBtn"
                    onClick={() => setUsersPage(usersPage + 1)}
                  >
                    Ver más usuarios
                  </button>
                </div>
              ) : null;
            })()}

            {users.filter(
              (u) =>
                u.name?.toLowerCase().includes(searchTerm) ||
                u.username?.toLowerCase().includes(searchTerm) ||
                u.documento?.toString().includes(searchTerm) ||
                u.email?.toLowerCase().includes(searchTerm)
            ).length === 0 && (
              <div className="empty-state">
                <p>No se encontraron usuarios</p>
              </div>
            )}

            {/* MODAL EDITAR USUARIO */}
            {showEditModal && editingUser && (
              <div className="modalOverlay" onClick={() => setShowEditModal(false)}>
                <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                  <h3>Editar Usuario</h3>
                  <div className="modalForm">
                    <div className="formSection">
                      <h4 className="sectionTitle">Información Personal</h4>
                      <div className="formField">
                        <label>Nombre Completo</label>
                        <input
                          type="text"
                          name="name"
                          value={editUserData.name || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Username</label>
                        <input
                          type="text"
                          name="username"
                          value={editUserData.username || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Documento</label>
                        <input
                          type="text"
                          name="documento"
                          value={editUserData.documento || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Fecha de Nacimiento</label>
                        <input
                          type="date"
                          name="fechaNacimiento"
                          value={editUserData.fechaNacimiento || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Edad</label>
                        <input
                          type="number"
                          name="age"
                          value={editUserData.age || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Estado Civil</label>
                        <input
                          type="text"
                          name="estadoCivil"
                          value={editUserData.estadoCivil || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>

                    <div className="formSection">
                      <h4 className="sectionTitle">Información de Contacto</h4>
                      <div className="formField">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={editUserData.email || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Teléfono</label>
                        <input
                          type="text"
                          name="phone"
                          value={editUserData.phone || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Celular Corporativo</label>
                        <input
                          type="text"
                          name="celularCorporativo"
                          value={editUserData.celularCorporativo || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>País</label>
                        <input
                          type="text"
                          name="country"
                          value={editUserData.country || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Dirección Personal</label>
                        <input
                          type="text"
                          name="direccionPersonal"
                          value={editUserData.direccionPersonal || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Dirección Laboral</label>
                        <input
                          type="text"
                          name="direccionLaboral"
                          value={editUserData.direccionLaboral || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>

                    <div className="formSection">
                      <h4 className="sectionTitle">Información Laboral</h4>
                      <div className="formField">
                        <label>Fecha de Ingreso</label>
                        <input
                          type="date"
                          name="fechaIngreso"
                          value={editUserData.fechaIngreso || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Cargo</label>
                        <input
                          type="text"
                          name="cargo"
                          value={editUserData.cargo || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Área</label>
                        <input
                          type="text"
                          name="area"
                          value={editUserData.area || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>

                    <div className="formSection">
                      <h4 className="sectionTitle">Otros Datos</h4>
                      <div className="formField">
                        <label>LinkedIn</label>
                        <input
                          type="text"
                          name="linkedIn"
                          value={editUserData.linkedIn || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Idioma</label>
                        <input
                          type="text"
                          name="language"
                          value={editUserData.language || ""}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div className="formField">
                        <label>Estado</label>
                        <select
                          name="estado"
                          value={editUserData.estado || "activo"}
                          onChange={handleEditUserChange}
                        >
                          <option value="activo">Activo</option>
                          <option value="inactivo">Inactivo</option>
                        </select>
                      </div>
                      <div className="formField">
                        <label>Rol</label>
                        <select
                          name="rol"
                          value={editUserData.rol || "empleado"}
                          onChange={handleEditUserChange}
                        >
                          <option value="admin">Admin</option>
                          <option value="empleado">Empleado</option>
                          <option value="temporal">Temporal</option>
                        </select>
                      </div>
                      <div className="formField">
                        <label>Puntos Yale</label>
                        <input
                          type="number"
                          name="points"
                          value={editUserData.points !== undefined ? editUserData.points : 0}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>

                    <div className="formSection encargadoSection">
                      <h4 className="sectionTitle">Espacios que administra</h4>
                      {editUserData.rol === "admin" ? (
                        <p className="formHint">
                          Un administrador ya gestiona todos los espacios; no hace falta asignarle ninguno.
                        </p>
                      ) : spaces.length === 0 ? (
                        <p className="formHint">Todavía no hay espacios creados.</p>
                      ) : (
                        <>
                          <p className="formHint">
                            En los espacios marcados esta persona puede publicar, moderar publicaciones y
                            comentarios, y gestionar la galería, los documentos y las encuestas. No obtiene
                            acceso al panel de administración.
                          </p>
                          <div className="encargadoAtajos">
                            <button type="button" onClick={() => setEspaciosDelEditado(spaces.map((s) => s.id))}>
                              Marcar todos
                            </button>
                            <button type="button" onClick={() => setEspaciosDelEditado([])}>
                              Quitar todos
                            </button>
                            <span>
                              {espaciosDelEditado.length} de {spaces.length}
                            </span>
                          </div>
                          <div className="encargadoLista">
                            {spaces.map((s) => (
                              <label key={s.id} className="encargadoItem">
                                <input
                                  type="checkbox"
                                  checked={espaciosDelEditado.includes(s.id)}
                                  onChange={(e) =>
                                    setEspaciosDelEditado((prev) =>
                                      e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id)
                                    )
                                  }
                                />
                                <span>{s.name}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button className="cancelBtn" onClick={() => setShowEditModal(false)}>Cancelar</button>
                    <button className="saveBtn" onClick={saveEditedUser}>Guardar Cambios</button>
                  </div>
                </div>
              </div>
            )}

            {/* AVISO INTEGRADO */}
            {showSuccessModal && (
              <div className="modalOverlay" onClick={() => setShowSuccessModal(false)}>
                <div className={`modalContent successModal`} onClick={(e) => e.stopPropagation()}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{successMessage.icon || "✓"}</div>
                  <h3 style={{ marginTop: 0 }}>{successMessage.title || "Operación exitosa"}</h3>
                  <p style={{ marginBottom: 16 }}>{successMessage.message || "Los cambios se han aplicado correctamente."}</p>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button className="saveBtn" onClick={() => setShowSuccessModal(false)}>Aceptar</button>
                  </div>
                </div>
              </div>
            )}

            {/* MODAL ELIMINAR USUARIO */}
            {showDeleteModal && (
              <div className="modalOverlay" onClick={() => setShowDeleteModal(false)}>
                <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                  <h3>Eliminar Usuario</h3>
                  <p>¿Estás seguro de que deseas eliminar a <strong>{deleteUserName}</strong>?</p>
                  <p style={{ color: "#dc3545", fontSize: "0.9rem", marginBottom: "24px", fontWeight: "500" }}>Esta acción no se puede deshacer.</p>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button className="cancelBtn" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
                    <button className="deleteConfirmBtn" onClick={confirmDeleteUser}>Eliminar Usuario</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {view === "spaces" && (
          <section className="spaces-section">
            <div className="spaces-header">
              <h3>Espacios</h3>
              <button
                className="createSpaceBtn"
                onClick={openCreateSpaceModal}
              >
                Nuevo Espacio
              </button>
            </div>

            <div className="spaces-grid">
              {spaces && spaces.length > 0 ? (
                spaces.map((s) => (
                  <div key={s.id} className="space-card">
                    <div className="space-header">
                      <h4>{s.name}</h4>
                      <div className="space-actions">
                        <button
                          className="actionBtn editBtn"
                          onClick={() => openEditSpaceModal(s)}
                          title="Editar"
                        >
                          <Edit fontSize="small" />
                        </button>
                        <button
                          className="actionBtn deleteBtn"
                          onClick={() => openDeleteSpaceModal(s.id, s.name)}
                          title="Eliminar"
                        >
                          <Delete fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className="space-content">
                      <p className="space-description">
                        {s.description || "Sin descripción"}
                      </p>
                      <p className="space-encargados">
                        <strong>Encargados:</strong>{" "}
                        {encargadosDe(s.id).length > 0
                          ? encargadosDe(s.id).map((e) => e.name).join(", ")
                          : "ninguno. Se asignan al editar un usuario."}
                      </p>
                      {s.image && (
                        <img
                          src={urlDeArchivo(s.image)}
                          alt={s.name}
                          className="space-image"
                          onError={(e) => (e.target.style.display = "none")}
                        />
                      )}
                    </div>

                    <div className="space-footer">
                      <span className="space-id">ID: {s.id}</span>
                      <span className="space-type">Comunidad</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No hay espacios creados aún</p>
                  <small>Crea tu primer espacio para comenzar</small>
                </div>
              )}
            </div>
          </section>
        )}

        {view === "notifications" && (
          <section className="notifications-section">
            <div className="section-head">
              <h3>Notificaciones</h3>
              <p>Elige una plantilla, revisa cómo se ve y define a quién se envía.</p>
            </div>

            <div className="notif-layout">
              {/* ==================== Plantilla y vista previa ==================== */}
              <div className="notif-panel">
                <div className="notif-panel-head">
                  <Visibility fontSize="small" />
                  <h4>Plantilla</h4>
                </div>

                <div className="notif-panel-body">
                  <div className="template-tabs" role="tablist">
                    {PLANTILLAS.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={selectedTemplate === t.id}
                        className={`template-tab ${selectedTemplate === t.id ? "active" : ""}`}
                        onClick={() => setSelectedTemplate(t.id)}
                      >
                        {t.etiqueta}
                      </button>
                    ))}
                  </div>

                  <div className="template-preview">
                    {selectedTemplate === "welcome" && (
                      <MailMock
                        titulo="Bienvenido a ASSA ABLOY"
                        subtitulo="Abriendo puertas hacia el futuro"
                        destacado={
                          <>
                            <h5>Credenciales de acceso</h5>
                            <p><strong>Usuario:</strong> <code>{previewData.usuario}</code></p>
                            <p><strong>Contraseña:</strong> <code>{previewData.password}</code></p>
                          </>
                        }
                        cta="Iniciar sesión"
                      >
                        <h3>Hola {previewData.nombre}</h3>
                        <p>Tu cuenta ha sido creada correctamente en el sistema.</p>
                      </MailMock>
                    )}

                    {selectedTemplate === "birthday" && (
                      <>
                        {bloqueAleatorio}

                        {birthdayPreviewHtml ? (
                          <TarjetaPreview
                            html={birthdayPreviewHtml}
                            canvas={BIRTHDAY_TEMPLATE_CANVAS}
                            titulo="Vista previa del correo de cumpleaños"
                          />
                        ) : (
                          <div className="template-preview__cargando">
                            <CircularProgress size={22} />
                            <span>Cargando plantilla…</span>
                          </div>
                        )}
                      </>
                    )}

                    {selectedTemplate === "anniversary" && (
                      <>
                        {bloqueAleatorio}

                        {anniversaryPreviewHtml ? (
                          <TarjetaPreview
                            html={anniversaryPreviewHtml}
                            canvas={ANNIVERSARY_TEMPLATE_CANVAS}
                            titulo="Vista previa del correo de aniversario"
                          />
                        ) : (
                          <div className="template-preview__cargando">
                            <CircularProgress size={22} />
                            <span>Cargando plantilla…</span>
                          </div>
                        )}
                      </>
                    )}

                    {selectedTemplate === "mention" && (
                      <MailMock
                        titulo="Te han mencionado"
                        subtitulo="En una publicación de ASSA ABLOY"
                        destacado={
                          <>
                            <h5>Publicación</h5>
                            <p>Contenido de la publicación donde fuiste mencionado.</p>
                          </>
                        }
                        cta="Ver publicación"
                      >
                        <h3>Hola {previewData.nombre}</h3>
                        <p>
                          <strong>{previewData.mentionedBy}</strong> te ha mencionado en una
                          publicación en {previewData.spaceName}.
                        </p>
                      </MailMock>
                    )}

                    {selectedTemplate === "publication" && (
                      <MailMock
                        titulo={previewData.postTitle || "Nueva publicación"}
                        subtitulo={`Espacio: ${previewData.spaceName || "General"}`}
                        destacado={
                          <>
                            <h5>Vista previa</h5>
                            <p>{previewData.postContent || "Se publicó una actualización importante para el equipo."}</p>
                          </>
                        }
                        cta="Ver publicación"
                      >
                        <h3>Hola {previewData.nombre}</h3>
                        <p>
                          {previewData.mentionedBy || "Carlos Rodríguez"} publicó contenido nuevo
                          en {previewData.spaceName || "General"}.
                        </p>
                      </MailMock>
                    )}
                  </div>
                </div>
              </div>

              {/* ==================== Configuración de envío ==================== */}
              <div className="notif-panel">
                <div className="notif-panel-head">
                  <Send fontSize="small" />
                  <h4>Envío</h4>
                </div>

                <div className="notif-panel-body">
                  {/* El espacio solo se pide donde de verdad cambia algo: en las
                      celebraciones decide dónde se publica la felicitación y en
                      "Publicación" es el espacio que dispara el aviso. Para las
                      demás plantillas era un selector que no hacía nada. */}
                  {(esCelebracion || selectedTemplate === "publication") && spaces.length > 0 && (
                    <div className="notif-field">
                      <label className="notif-label">
                        Espacio {selectedTemplate === "publication" ? "" : "(opcional)"}
                      </label>
                      <p className="notif-help">
                        {selectedTemplate === "publication"
                          ? "Las publicaciones de este espacio son las que generan el correo."
                          : "Además del correo, la felicitación se publica en este espacio."}
                      </p>
                      <div className="chip-grid">
                        {selectedTemplate !== "publication" && (
                          <button
                            type="button"
                            className={`chip ${selectedSpace === "" ? "active" : ""}`}
                            onClick={() => handleSelectSpace("")}
                          >
                            Sin espacio
                          </button>
                        )}
                        {spaces.map((space) => (
                          <button
                            type="button"
                            key={space.id}
                            title={space.name}
                            className={`chip ${Number(selectedSpace) === Number(space.id) ? "active" : ""}`}
                            onClick={() => handleSelectSpace(Number(space.id))}
                          >
                            {space.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* -------- Celebraciones: configuración del envío automático --------
                      Cumpleaños y aniversario NO se mandan a mano. El proceso
                      diario revisa quién cumple y aplica lo que se guarde aquí,
                      así que esta pantalla no envía nada: solo deja escrito a
                      quién habrá que enviarle cuando llegue el día. */}
                  {esCelebracion && (
                    <>
                      <div className="notif-alert info">
                        <ScheduleIcon fontSize="small" />
                        <span>
                          Este correo se envía solo, el día que corresponde. Aquí defines a
                          quién le llega y se guarda para todos los envíos siguientes.
                        </span>
                      </div>

                      <div className="notif-field">
                        <label className="notif-check">
                          <input
                            type="checkbox"
                            checked={celebConfig.isActive}
                            onChange={(e) => setCelebConfig({ ...celebConfig, isActive: e.target.checked })}
                          />
                          <span>
                            Envío automático activo
                            {!celebConfig.isActive && " — ahora mismo no se enviará ningún correo"}
                          </span>
                        </label>
                      </div>

                      <div className="notif-field">
                        <label className="notif-label">Para (destinatarios principales)</label>
                        <select
                          className="notif-input"
                          value={celebConfig.audience}
                          onChange={(e) => setCelebConfig({ ...celebConfig, audience: e.target.value })}
                        >
                          <option value="celebrant">
                            Solo la persona que {selectedTemplate === "birthday" ? "cumple años" : "cumple el aniversario"}
                          </option>
                          <option value="all_users">Todos los colaboradores activos</option>
                          <option value="specific_users">Colaboradores específicos</option>
                        </select>

                        {celebConfig.audience === "specific_users" && (
                          <>
                            <select
                              multiple
                              className="notif-input notif-input--multi"
                              value={celebConfig.recipientUserIds.map(String)}
                              onChange={(e) =>
                                setCelebConfig({
                                  ...celebConfig,
                                  recipientUserIds: Array.from(e.target.selectedOptions).map((o) => Number(o.value)),
                                })
                              }
                            >
                              {users
                                .filter((u) => Boolean(u?.email))
                                .map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.name || u.username || `Usuario ${u.id}`} — {u.email}
                                  </option>
                                ))}
                            </select>
                            <p className="notif-help">
                              Seleccionados: {celebConfig.recipientUserIds.length}. Mantén pulsada la tecla
                              Ctrl (o Cmd) para elegir varios.
                            </p>
                          </>
                        )}

                        <input
                          type="text"
                          className="notif-input"
                          value={celebConfig.toEmails}
                          onChange={(e) => setCelebConfig({ ...celebConfig, toEmails: e.target.value })}
                          placeholder="Otros correos: gerencia@assaabloy.com"
                        />

                        <p className="notif-help">
                          La persona homenajeada recibe el correo siempre, además de quien elijas o
                          escribas aquí.
                        </p>
                      </div>

                      <div className="notif-field">
                        <label className="notif-label">Copia y copia oculta (opcional)</label>
                        <input
                          type="text"
                          className="notif-input"
                          value={celebConfig.ccEmails}
                          onChange={(e) => setCelebConfig({ ...celebConfig, ccEmails: e.target.value })}
                          placeholder="Copia (CC): talento.humano@assaabloy.com"
                        />
                        <input
                          type="text"
                          className="notif-input"
                          value={celebConfig.bccEmails}
                          onChange={(e) => setCelebConfig({ ...celebConfig, bccEmails: e.target.value })}
                          placeholder="Copia oculta (CCO): jefatura@assaabloy.com"
                        />
                        <p className="notif-help">
                          Separa varios correos con comas. Quien va en copia oculta no aparece
                          para el resto de destinatarios.
                        </p>
                      </div>

                      {/* Solo Aniversario: años que cuentan como hito. */}
                      {selectedTemplate === "anniversary" && (
                        <div className="notif-field">
                          <label className="notif-label">Años que cuentan como hito</label>
                          <div className="chip-grid">
                            {[1, 5, 10, 15, 20, 25, 30].map((yr) => {
                              const activo = celebConfig.milestoneYears.includes(yr);
                              return (
                                <button
                                  type="button"
                                  key={yr}
                                  className={`chip ${activo ? "active" : ""}`}
                                  onClick={() =>
                                    setCelebConfig({
                                      ...celebConfig,
                                      milestoneYears: activo
                                        ? celebConfig.milestoneYears.filter((y) => y !== yr)
                                        : [...celebConfig.milestoneYears, yr].sort((a, b) => a - b),
                                    })
                                  }
                                >
                                  {yr} {yr === 1 ? "año" : "años"}
                                </button>
                              );
                            })}
                          </div>
                          <p className="notif-help">
                            {celebConfig.milestoneYears.length === 0
                              ? "Ninguno marcado: se felicita cualquier año de antigüedad."
                              : `Solo se felicita a quien cumpla ${celebConfig.milestoneYears.join(", ")} años.`}
                          </p>
                        </div>
                      )}

                      <button
                        type="button"
                        className="notif-btn primary"
                        onClick={guardarConfigCelebracion}
                        disabled={celebConfigSaving}
                      >
                        {celebConfigSaving ? "Guardando…" : "Guardar configuración"}
                      </button>

                      {celebConfigMsg && (
                        <div className={`notif-alert ${celebConfigMsg.ok ? "ok" : "error"}`}>
                          {celebConfigMsg.ok ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                          <span>{celebConfigMsg.texto}</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* -------- Publicación: envío automático al publicar -------- */}
                  {selectedTemplate === "publication" && (
                    <div className="notif-field">
                      <label className="notif-label">Aviso automático</label>
                      <label className="notif-check">
                        <input
                          type="checkbox"
                          checked={Boolean(emailConfig.publicationActive)}
                          onChange={(e) => setEmailConfig({ ...emailConfig, publicationActive: e.target.checked })}
                        />
                        <span>Avisar por correo cada vez que se publique en este espacio</span>
                      </label>

                      <p className="notif-help">¿A quién se avisa?</p>
                      <select
                        className="notif-input"
                        value={emailConfig.distributionType}
                        onChange={(e) => setEmailConfig({ ...emailConfig, distributionType: e.target.value })}
                      >
                        <option value="space_users">Solo a los miembros del espacio</option>
                        <option value="all_users">A todos los colaboradores</option>
                        <option value="specific_users">Solo a los que yo elija</option>
                      </select>

                      {emailConfig.distributionType === "specific_users" && (
                        <>
                          <select
                            multiple
                            className="notif-input notif-input--multi"
                            value={selectedUserIds.map(String)}
                            onChange={(e) =>
                              setSelectedUserIds(
                                Array.from(e.target.selectedOptions).map((o) => Number(o.value))
                              )
                            }
                          >
                            {users
                              .filter((u) => Boolean(u?.email))
                              .map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.name || u.username || `Usuario ${u.id}`} — {u.email}
                                </option>
                              ))}
                          </select>
                          <p className="notif-help">Seleccionados: {selectedUserIds.length}</p>
                        </>
                      )}

                      <button
                        type="button"
                        className="notif-btn primary"
                        onClick={savePublicationConfig}
                        disabled={!selectedSpace}
                      >
                        Guardar configuración
                      </button>
                      {!selectedSpace && (
                        <p className="notif-help">Elige primero un espacio arriba.</p>
                      )}
                    </div>
                  )}

                  {/* -------- Prueba (nunca toca a usuarios reales) --------
                      La bienvenida se dispara sola al crear un usuario y ya se
                      ve entera en la vista previa de al lado, así que no
                      necesita un envío de prueba propio. */}
                  {selectedTemplate !== "welcome" && (
                    <div className={`notif-field ${esCelebracion || selectedTemplate === "publication" ? "notif-field--divider" : ""}`}>
                      <label className="notif-label">Probar antes de enviar</label>
                      <p className="notif-help">
                        Manda la plantilla «{etiquetaPlantilla(selectedTemplate)}» con datos de
                        ejemplo a un solo correo. No afecta a los colaboradores ni cuenta como
                        felicitación enviada.
                      </p>
                      <div className="notif-inline">
                        <input
                          type="email"
                          className="notif-input"
                          value={testSendEmail}
                          onChange={(e) => setTestSendEmail(e.target.value)}
                          placeholder="correo@ejemplo.com"
                        />
                        <button
                          type="button"
                          className="notif-btn primary"
                          onClick={handleSendTestEmail}
                          disabled={testSendLoading}
                        >
                          {testSendLoading ? "Enviando…" : "Enviar prueba"}
                        </button>
                      </div>

                      {testSendResult && (
                        <div className={`notif-alert ${testSendResult.ok ? "ok" : "error"}`}>
                          {testSendResult.ok ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                          <span>{testSendResult.message}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTemplate === "welcome" && (
                    <div className="notif-alert info">
                      <ScheduleIcon fontSize="small" />
                      <span>
                        Este correo sale solo cuando se crea un colaborador nuevo, con sus
                        credenciales. No hay nada que configurar aquí.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============================ REGISTROS ============================
            Todo lo que la plataforma ya hizo: correos que salieron y
            movimientos de puntos. Es una pantalla de consulta, sin acciones
            que cambien nada, por eso vive aparte de las de configuración. */}
        {view === "logs" && (
          <section className="logs-section">
            <div className="section-head">
              <h3>Registros del sistema</h3>
              <p>
                Bitácora de auditoría: correos que salieron, movimientos de puntos, quién
                entró a la plataforma y qué cambió cada administrador. Es solo de consulta:
                desde aquí no se modifica nada.
              </p>
            </div>

            <div className="logs-tabs">
              <button
                type="button"
                className={`logs-tab ${logsTab === "emails" ? "active" : ""}`}
                onClick={() => setLogsTab("emails")}
              >
                Correos enviados
              </button>
              <button
                type="button"
                className={`logs-tab ${logsTab === "points" ? "active" : ""}`}
                onClick={() => setLogsTab("points")}
              >
                Movimientos de puntos
              </button>
              <button
                type="button"
                className={`logs-tab ${logsTab === "acceso" ? "active" : ""}`}
                onClick={() => setLogsTab("acceso")}
              >
                Accesos
              </button>
              <button
                type="button"
                className={`logs-tab ${logsTab === "accion" ? "active" : ""}`}
                onClick={() => setLogsTab("accion")}
              >
                Acciones de administración
              </button>
              <button
                type="button"
                className="notif-btn ghost logs-refresh"
                onClick={() => {
                  if (logsTab === "emails") fetchEmailHistory();
                  else if (logsTab === "points") fetchPointsLog();
                  else fetchActivityLog(logsTab);
                }}
                disabled={
                  logsTab === "emails" ? emailHistoryLoading
                    : logsTab === "points" ? pointsLogLoading
                    : activityLoading
                }
              >
                {(logsTab === "emails" ? emailHistoryLoading
                  : logsTab === "points" ? pointsLogLoading
                  : activityLoading)
                  ? "Cargando…"
                  : "Actualizar"}
              </button>
            </div>

            {logsTab === "emails" && (
              <div className="logs-panel">
                <p className="notif-help">
                  Cada correo que sale de la plataforma —de prueba, automático o masivo— queda
                  aquí con sus destinatarios y una copia exacta de cómo se veía.
                </p>

                {emailHistoryError && (
                  <div className="notif-alert error">
                    <Cancel fontSize="small" />
                    <span>{emailHistoryError}</span>
                  </div>
                )}

                {!emailHistoryLoading && !emailHistoryError && emailHistory.length === 0 && (
                  <div className="logs-empty">Todavía no se ha enviado ningún correo desde la plataforma.</div>
                )}

                {emailHistory.length > 0 && (
                  <div className="logs-scroll">
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Plantilla</th>
                          <th>Para</th>
                          <th>Copia</th>
                          <th>Estado</th>
                          <th aria-label="Acciones" />
                        </tr>
                      </thead>
                      <tbody>
                        {emailHistory.map((item) => (
                          <tr key={item.id}>
                            <td className="nowrap">
                              {formatearFechaHora(item.createdAt)}
                            </td>
                            <td className="nowrap">
                              <span className="logs-plantilla">
                                {EMAIL_TEMPLATE_LABELS[item.templateType] || item.templateType}
                              </span>
                              {Boolean(item.isTest) && <span className="logs-tag test">Prueba</span>}
                              {Boolean(item.isBulk) && <span className="logs-tag bulk">Masivo</span>}
                            </td>
                            <td className="truncate" title={item.toEmails || ""}>{item.toEmails || "—"}</td>
                            <td className="truncate muted" title={item.ccEmails || ""}>{item.ccEmails || "—"}</td>
                            <td className="nowrap">
                              {item.status === "sent" ? (
                                <span className="logs-estado ok"><CheckCircle fontSize="small" /> Enviado</span>
                              ) : (
                                <span className="logs-estado error" title={item.errorMessage || ""}>
                                  <Cancel fontSize="small" /> Falló
                                </span>
                              )}
                            </td>
                            <td className="right">
                              <button type="button" className="logs-verBtn" onClick={() => openHistoryPreview(item.id)}>
                                <Visibility fontSize="small" /> Ver
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {logsTab === "points" && (
              <div className="logs-panel">
                <p className="notif-help">
                  Cada carga o descuento de puntos Yale, con su motivo y quién lo registró. Es lo
                  mismo que el colaborador ve en su billetera, pero de toda la organización.
                </p>

                {pointsLogError && (
                  <div className="notif-alert error">
                    <Cancel fontSize="small" />
                    <span>{pointsLogError}</span>
                  </div>
                )}

                {!pointsLogLoading && !pointsLogError && pointsLog.length === 0 && (
                  <div className="logs-empty">Todavía no se ha registrado ningún movimiento de puntos.</div>
                )}

                {pointsLog.length > 0 && (
                  <div className="logs-scroll">
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Colaborador</th>
                          <th>Documento</th>
                          <th className="right">Movimiento</th>
                          <th className="right">Saldo</th>
                          <th>Motivo</th>
                          <th>Registrado por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pointsLog.map((m) => (
                          <tr key={m.id}>
                            <td className="nowrap">
                              {formatearFechaHora(m.createdAt)}
                            </td>
                            <td className="truncate" title={m.userName || ""}>{m.userName || "—"}</td>
                            <td className="nowrap muted">{m.userDocumento || "—"}</td>
                            <td className={`right nowrap logs-delta ${m.delta >= 0 ? "sube" : "baja"}`}>
                              {m.delta > 0 ? "+" : ""}{m.delta}
                            </td>
                            <td className="right nowrap">{m.balanceAfter}</td>
                            <td className="truncate" title={m.reason || ""}>{m.reason || "—"}</td>
                            <td className="truncate muted" title={m.createdByName || ""}>
                              {m.createdByName || "Sistema"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {(logsTab === "acceso" || logsTab === "accion") && (
              <div className="logs-panel">
                <p className="notif-help">
                  {logsTab === "acceso"
                    ? "Cada intento de inicio de sesión: quién entró, desde qué dirección IP y si lo consiguió. Los intentos fallidos ayudan a detectar a alguien probando contraseñas."
                    : "Cada cambio que hace un administrador sobre los datos de otros: altas y bajas de colaboradores, activaciones, contraseñas, cargas de puntos, espacios y configuración de correos."}
                </p>

                {activityError && (
                  <div className="notif-alert error">
                    <Cancel fontSize="small" />
                    <span>{activityError}</span>
                  </div>
                )}

                {!activityLoading && !activityError && activityLog[logsTab].length === 0 && (
                  <div className="logs-empty">
                    {logsTab === "acceso"
                      ? "Todavía no hay inicios de sesión registrados."
                      : "Todavía no hay acciones de administración registradas."}
                  </div>
                )}

                {activityLog[logsTab].length > 0 && (
                  <div className="logs-scroll">
                    <table className="logs-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>{logsTab === "acceso" ? "Cuenta" : "Administrador"}</th>
                          <th>{logsTab === "acceso" ? "Resultado" : "Acción"}</th>
                          <th>Detalle</th>
                          {logsTab === "acceso" && <th>Origen</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {activityLog[logsTab].map((r) => (
                          <tr key={r.id}>
                            <td className="nowrap">
                              {formatearFechaHora(r.createdAt)}
                            </td>
                            <td className="truncate" title={r.userName || ""}>
                              {r.userName || "—"}
                              {r.userUsername && <span className="muted"> @{r.userUsername}</span>}
                            </td>
                            <td className="nowrap">
                              <span className={`logs-estado ${r.success ? "ok" : "error"}`}>
                                {r.success ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                                {ACCIONES_LEGIBLES[r.action] || r.action}
                              </span>
                            </td>
                            <td className="truncate" title={r.detail || ""}>{r.detail || "—"}</td>
                            {logsTab === "acceso" && (
                              <td className="nowrap muted">{r.ip || "—"}</td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {showHistoryPreview && (
          <div className="modalOverlay" onClick={closeHistoryPreview}>
            <div
              className="modalContent"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "900px", width: "94%", maxHeight: "88vh", display: "flex", flexDirection: "column" }}
            >
              <div className="modalHeader" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                  <h3 style={{ color: "#005fa3", margin: 0, fontSize: "19px", fontWeight: 600 }}>
                    Vista previa del correo enviado
                  </h3>
                  {historyDetail && !historyDetail.error && (
                    <p style={{ margin: "6px 0 0", color: "#666", fontSize: "13px" }}>
                      {formatearFechaHora(historyDetail.createdAt)} · Enviado por {historyDetail.sentByName || "Sistema"}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeHistoryPreview}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "#888" }}
                >
                  <Close />
                </button>
              </div>

              {historyDetailLoading && (
                <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>Cargando...</div>
              )}

              {!historyDetailLoading && historyDetail?.error && (
                <div style={{ padding: "20px", color: "#c0392b" }}>{historyDetail.error}</div>
              )}

              {!historyDetailLoading && historyDetail && !historyDetail.error && (
                <>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "10px",
                    fontSize: "13px",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "14px",
                    margin: "14px 0"
                  }}>
                    <div><strong style={{ color: "#005fa3" }}>Plantilla:</strong> {EMAIL_TEMPLATE_LABELS[historyDetail.templateType] || historyDetail.templateType}</div>
                    <div><strong style={{ color: "#005fa3" }}>Asunto:</strong> {historyDetail.subject || '—'}</div>
                    <div><strong style={{ color: "#005fa3" }}>Para:</strong> {historyDetail.toEmails || '—'}</div>
                    <div><strong style={{ color: "#005fa3" }}>CC:</strong> {historyDetail.ccEmails || '—'}</div>
                    <div><strong style={{ color: "#005fa3" }}>CCO:</strong> {historyDetail.bccEmails || '—'}</div>
                    <div>
                      <strong style={{ color: "#005fa3" }}>Estado:</strong>{" "}
                      {historyDetail.status === 'sent' ? 'Enviado correctamente' : `Falló: ${historyDetail.errorMessage || ''}`}
                    </div>
                  </div>

                  {historyDetail.html ? (
                    <div style={{ flex: 1, minHeight: "340px", border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" }}>
                      <iframe
                        title="Vista previa del correo"
                        srcDoc={htmlParaVistaPrevia(historyDetail.html)}
                        style={{ width: "100%", height: "50vh", border: "none" }}
                        sandbox=""
                      />
                    </div>
                  ) : (
                    <div style={{ padding: "20px", color: "#999", textAlign: "center" }}>
                      Este correo no tenía contenido HTML (era un correo de texto plano).
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {showCreateModal && (
          <div className="modalOverlay" onClick={() => setShowCreateModal(false)}>
            <div className="modalContent createUserModal" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h3 style={{ color: "#005fa3", margin: 0, fontSize: "24px", fontWeight: "600" }}>
                  Crear Nuevo Usuario
                </h3>
                <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
                  Completa los datos del nuevo usuario
                </p>
              </div>
              
              {createError && (
                <div className="errorMessage" style={{ 
                  color: "#d32f2f", 
                  background: "#ffebee", 
                  padding: "14px 16px", 
                  borderRadius: "8px", 
                  margin: "0 0 20px",
                  border: "1px solid #ffcdd2",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}>
                  {createError}
                </div>
              )}
              
              <div className="modalForm" style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "8px" }}>
                {/* CAMPOS REQUERIDOS */}
                <div className="fieldsSection" style={{ background: "#f8f9fa", padding: "20px", borderRadius: "8px", marginBottom: "16px" }}>
                  <h4 style={{ color: "#005fa3", margin: "0 0 16px", fontSize: "16px", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                    Información Básica <span style={{ color: "#d32f2f", fontSize: "12px" }}>(Requerida)</span>
                  </h4>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                        Nombre Completo *
                      </label>
                      <input
                        name="name"
                        value={newUser.name}
                        onChange={handleNewUserChange}
                        placeholder="Ej: Juan Carlos Pérez Gómez"
                        style={{ 
                          width: "100%", 
                          padding: "12px", 
                          border: "1px solid #ccc", 
                          borderRadius: "6px", 
                          fontSize: "14px",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                        Usuario *
                      </label>
                      <input
                        name="username"
                        value={newUser.username}
                        onChange={handleNewUserChange}
                        placeholder="Ej: jperez"
                        style={{ 
                          width: "100%", 
                          padding: "12px", 
                          border: "1px solid #ccc", 
                          borderRadius: "6px", 
                          fontSize: "14px",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                        Documento *
                      </label>
                      <input
                        name="documento"
                        value={newUser.documento}
                        onChange={handleNewUserChange}
                        placeholder="Ej: 1234567890"
                        style={{ 
                          width: "100%", 
                          padding: "12px", 
                          border: "1px solid #ccc", 
                          borderRadius: "6px", 
                          fontSize: "14px",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                    
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                        Correo Electrónico *
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={newUser.email}
                        onChange={handleNewUserChange}
                        placeholder="Ej: juan.perez@assaabloy.com"
                        style={{ 
                          width: "100%", 
                          padding: "12px", 
                          border: "1px solid #ccc", 
                          borderRadius: "6px", 
                          fontSize: "14px",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* CAMPOS OPCIONALES - COLAPSABLES */}
                <div className="fieldsSection optional" style={{ marginBottom: "16px" }}>
                  <button
                    type="button"
                    className="toggleOptionalBtn"
                    onClick={() => setShowOptionalFields(!showOptionalFields)}
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      background: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#005fa3",
                      transition: "all 0.2s"
                    }}
                  >
                    <span className="arrow" style={{ 
                      transition: "transform 0.2s",
                      transform: showOptionalFields ? "rotate(90deg)" : "rotate(0deg)"
                    }}><ChevronRight fontSize="small" style={{ display: "block" }} /></span>
                    <span>Campos Opcionales</span>
                    <span style={{ marginLeft: "auto", fontSize: "12px", color: "#999" }}>
                      {showOptionalFields ? "Ocultar" : "Mostrar"}
                    </span>
                  </button>
                  
                  {showOptionalFields && (
                    <div className="optionalFieldsContainer" style={{ 
                      background: "#f8f9fa", 
                      padding: "20px", 
                      borderRadius: "0 0 8px 8px",
                      marginTop: "4px",
                      border: "1px solid #e0e0e0",
                      borderTop: "none"
                    }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            Teléfono
                          </label>
                          <input
                            name="phone"
                            value={newUser.phone}
                            onChange={handleNewUserChange}
                            placeholder="Ej: +57 300 123 4567"
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            País
                          </label>
                          <input
                            name="country"
                            value={newUser.country}
                            onChange={handleNewUserChange}
                            placeholder="Ej: Colombia"
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>
                        
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            Dirección Laboral
                          </label>
                          <input
                            name="direccionLaboral"
                            value={newUser.direccionLaboral}
                            onChange={handleNewUserChange}
                            placeholder="Ej: Calle 123 #45-67, Bogotá"
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>
                        
                        <div style={{ gridColumn: "1 / -1" }}>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            LinkedIn
                          </label>
                          <input
                            name="linkedIn"
                            value={newUser.linkedIn}
                            onChange={handleNewUserChange}
                            placeholder="Ej: https://linkedin.com/in/usuario"
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            Fecha de Ingreso
                          </label>
                          <input
                            type="date"
                            name="fechaIngreso"
                            value={newUser.fechaIngreso}
                            onChange={handleNewUserChange}
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                            Fecha de Nacimiento
                          </label>
                          <input
                            type="date"
                            name="fechaNacimiento"
                            value={newUser.fechaNacimiento}
                            onChange={handleNewUserChange}
                            style={{ 
                              width: "100%", 
                              padding: "12px", 
                              border: "1px solid #ccc", 
                              borderRadius: "6px", 
                              fontSize: "14px",
                              boxSizing: "border-box"
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ROL Y CORREO */}
                <div className="fieldsSection" style={{ background: "#f8f9fa", padding: "20px", borderRadius: "8px" }}>
                  <h4 style={{ color: "#005fa3", margin: "0 0 16px", fontSize: "16px", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                    Configuración
                  </h4>
                  
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", marginBottom: "6px", color: "#666", fontSize: "13px", fontWeight: "500" }}>
                      Rol del Usuario
                    </label>
                    <select
                      name="rol"
                      value={newUser.rol}
                      onChange={handleNewUserChange}
                      style={{ 
                        width: "100%", 
                        padding: "12px", 
                        border: "1px solid #ccc", 
                        borderRadius: "6px", 
                        fontSize: "14px",
                        boxSizing: "border-box",
                        cursor: "pointer"
                      }}
                    >
                      <option value="empleado">Empleado</option>
                      <option value="admin">Administrador</option>
                      <option value="temporal">Temporal</option>
                    </select>
                  </div>

                  <label style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "10px", 
                    cursor: "pointer", 
                    padding: "12px",
                    background: "#fff",
                    borderRadius: "6px",
                    border: "1px solid #e0e0e0"
                  }}>
                    <input
                      type="checkbox"
                      name="sendWelcomeEmail"
                      checked={newUser.sendWelcomeEmail}
                      onChange={handleNewUserChange}
                      style={{ width: "20px", height: "20px", cursor: "pointer" }}
                    />
                    <div>
                      <div style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>
                        Enviar correo de bienvenida
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                        El usuario recibirá sus credenciales por email
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="modalActions" style={{ 
                display: "flex", 
                gap: "12px", 
                justifyContent: "flex-end",
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid #e0e0e0"
              }}>
                <button
                  className="cancelBtn"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowOptionalFields(false);
                    setCreateError("");
                  }}
                  style={{
                    padding: "12px 24px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#666"
                  }}
                >
                  Cancelar
                </button>
                <button 
                  className="saveBtn" 
                  onClick={handleCreateUser}
                  style={{
                    padding: "12px 24px",
                    background: "linear-gradient(135deg, #005fa3 0%, #0077c8 100%)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#fff"
                  }}
                >
                  Crear Usuario
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL IMPORTAR USUARIOS */}
        {showImportModal && (
          <div className="modalOverlay" onClick={() => !importLoading && closeImportModal()}>
            <div className="modalContent importModal" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h3 style={{ color: "#005fa3", margin: 0, fontSize: "24px", fontWeight: "600" }}>
                  <Upload style={{ verticalAlign: "middle", marginRight: "8px" }} />
                  {importMode === "pointsOnly" ? "Cargar Puntos Yale" : "Importar Usuarios"}
                </h3>
                <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
                  {importMode === "pointsOnly"
                    ? "Sube un archivo con el movimiento de puntos de cada colaborador y su motivo"
                    : "Carga un archivo CSV o Excel con los datos de los colaboradores"}
                </p>
              </div>

              {!importResults ? (
                <>
                  {/* Dos tareas distintas que comparten el mismo formulario:
                      dar de alta colaboradores o mover puntos. El modal se abre
                      ya en el modo correcto desde los botones de la cabecera;
                      este selector solo permite cambiar sin cerrarlo. */}
                  <div className="import-mode-toggle">
                    <button
                      type="button"
                      className={`import-mode ${importMode === "full" ? "active" : ""}`}
                      onClick={() => setImportMode("full")}
                    >
                      <span className="import-mode__titulo">Colaboradores</span>
                      <span className="import-mode__desc">Crea o actualiza el perfil completo</span>
                    </button>
                    <button
                      type="button"
                      className={`import-mode ${importMode === "pointsOnly" ? "active" : ""}`}
                      onClick={() => setImportMode("pointsOnly")}
                    >
                      <span className="import-mode__titulo">Puntos Yale</span>
                      <span className="import-mode__desc">Carga o descuenta puntos por documento</span>
                    </button>
                  </div>

                  <div className="import-instructions">
                    <h4>Formato del archivo</h4>
                    {importMode === "pointsOnly" ? (
                      <>
                        <p><strong>Columnas obligatorias:</strong> Documento, Puntos (+/-), Motivo.</p>
                        <p>
                          <strong>Puntos (+/-)</strong> es un movimiento, no el saldo final: un
                          número positivo suma y uno negativo resta. Cada fila queda guardada en el
                          historial del colaborador con su motivo, y se puede consultar después en
                          Registros. Solo afecta a colaboradores que ya existen: se buscan por
                          documento y no se crea a nadie nuevo.
                        </p>
                      </>
                    ) : (
                      <>
                        <p><strong>Columnas obligatorias:</strong> Nombre, Apellido, Documento, Puntos, Correo.</p>
                        <p><strong>Opcionales:</strong> Fecha de nacimiento, Fecha de ingreso, Cargo, Área.</p>
                      </>
                    )}

                    <div className="import-descargas">
                      {importMode === "pointsOnly" && (
                        <button type="button" className="notif-btn ghost" onClick={downloadCurrentPointsTemplate}>
                          <Download fontSize="small" /> Descargar saldos actuales
                        </button>
                      )}
                      <button type="button" className="notif-btn ghost" onClick={downloadCSVTemplate}>
                        <Download fontSize="small" /> Descargar plantilla vacía
                      </button>
                    </div>
                  </div>

                  <div className="file-upload-section">
                    <label
                      className="dropzone"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add("dragging");
                      }}
                      onDragLeave={(e) => e.currentTarget.classList.remove("dragging")}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("dragging");
                        if (e.dataTransfer.files.length > 0) {
                          const input = e.currentTarget.querySelector("input");
                          const dataTransfer = new DataTransfer();
                          dataTransfer.items.add(e.dataTransfer.files[0]);
                          input.files = dataTransfer.files;
                          handleFileChange({ target: input });
                        }
                      }}
                    >
                      <FileUpload className="dropzone__icono" />
                      <span className="dropzone__titulo">
                        {importFile ? importFile.name : "Selecciona o arrastra un archivo"}
                      </span>
                      <span className="dropzone__nota">CSV o Excel (.csv, .xlsx, .xls) · máximo 5 MB</span>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                      />
                    </label>

                    {importFile && (
                      <div className="import-archivo">
                        <span className="import-archivo__nombre">{importFile.name}</span>
                        <button type="button" onClick={() => setImportFile(null)}>Quitar</button>
                      </div>
                    )}
                  </div>

                  {importMode === "full" && (
                    <label className="notif-check import-opcion">
                      <input
                        type="checkbox"
                        checked={importUpdateExisting}
                        onChange={(e) => setImportUpdateExisting(e.target.checked)}
                      />
                      <span>Actualizar la información si el documento ya existe</span>
                    </label>
                  )}
                  <div className="modalActions" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button
                      className="cancelBtn"
                      onClick={closeImportModal}
                      disabled={importLoading}
                      style={{
                        padding: "12px 24px",
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        cursor: importLoading ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#666",
                        opacity: importLoading ? 0.5 : 1
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      className="saveBtn"
                      onClick={handleImportUsers}
                      disabled={!importFile || importLoading}
                      style={{
                        padding: "12px 24px",
                        background: !importFile || importLoading ? "#ccc" : "linear-gradient(135deg, #005fa3 0%, #0077c8 100%)",
                        border: "none",
                        borderRadius: "6px",
                        cursor: !importFile || importLoading ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      {importLoading ? (
                        <>
                          <CircularProgress size={16} style={{ color: "#fff" }} />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Upload fontSize="small" />
                          Importar
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* RESULTADOS DE LA IMPORTACIÓN */}
                  <div className="import-results" style={{ marginBottom: "20px" }}>
                    <div style={{
                      background: importResults.errors?.length > 0 ? "#fff3cd" : "#d4edda",
                      padding: "20px",
                      borderRadius: "8px",
                      border: importResults.errors?.length > 0 ? "1px solid #ffc107" : "1px solid #28a745",
                      marginBottom: "20px"
                    }}>
                      <h4 style={{
                        margin: "0 0 12px",
                        fontSize: "18px",
                        color: importResults.errors?.length > 0 ? "#856404" : "#155724"
                      }}>
                        {importResults.errors?.length > 0 ? "Importación completada con advertencias" : "Importación completada"}
                      </h4>
                      <div style={{ fontSize: "14px", lineHeight: "1.8" }}>
                        {importMode === "pointsOnly" ? (
                          <>
                            <div><strong>{importResults.successful || 0}</strong> movimientos de puntos registrados</div>
                            <div><strong>{importResults.skipped || 0}</strong> documentos sin usuario coincidente</div>
                            <div><strong>{importResults.failed || 0}</strong> filas con errores</div>
                          </>
                        ) : (
                          <>
                            <div><strong>{importResults.successful || 0}</strong> usuarios importados correctamente</div>
                            <div><strong>{importResults.skipped || 0}</strong> usuarios omitidos (ya existen)</div>
                            <div><strong>{importResults.failed || 0}</strong> usuarios con errores</div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Detalle antes/después de la recarga de puntos. Antes solo
                        se mostraban los totales, así que no había forma de
                        comprobar qué saldo tenía cada persona ni con cuál quedó. */}
                    {importMode === "pointsOnly" &&
                      importResults.detalle &&
                      importResults.detalle.length > 0 && (
                      <div className="importDetalle">
                        <h5 className="importDetalleTitulo">
                          Movimientos aplicados ({importResults.detalle.length})
                        </h5>
                        <div className="importDetalleScroll">
                          <table className="importDetalleTabla">
                            <thead>
                              <tr>
                                <th>Documento</th>
                                <th>Nombre</th>
                                <th className="num">Antes</th>
                                <th className="num">Movimiento</th>
                                <th className="num">Después</th>
                                <th>Motivo / Comentario</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importResults.detalle.map((d) => (
                                <tr key={`${d.documento}-${d.fila}`}>
                                  <td>{d.documento}</td>
                                  <td>{d.nombre}</td>
                                  <td className="num antes">{d.puntosAntes}</td>
                                  <td className={`num mov ${d.movimiento > 0 ? "sube" : "baja"}`}>
                                    {d.movimiento > 0 ? "+" : ""}{d.movimiento}
                                  </td>
                                  <td className="num despues">
                                    {d.puntosDespues}
                                    {d.ajustadoACero && (
                                      <span
                                        className="avisoCero"
                                        title="El descuento superaba el saldo disponible: se ajustó a 0"
                                      >
                                        !
                                      </span>
                                    )}
                                  </td>
                                  <td className="motivo">{d.motivo}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="importDetalleNota">
                          El texto de «Motivo / Comentario» queda guardado y es lo que
                          cada empleado verá en su historial de puntos.
                        </p>
                      </div>
                    )}

                    {importResults.errors && importResults.errors.length > 0 && (
                      <div style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        background: "#f8f9fa",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6"
                      }}>
                        <h5 style={{ margin: "0 0 12px", fontSize: "14px", color: "#d32f2f" }}>
                          Errores encontrados:
                        </h5>
                        {importResults.errors.map((error, idx) => (
                          <div key={idx} style={{
                            padding: "8px 12px",
                            background: "#fff",
                            borderLeft: "3px solid #d32f2f",
                            marginBottom: "8px",
                            borderRadius: "4px",
                            fontSize: "13px"
                          }}>
                            <strong>Fila {error.row}:</strong> {error.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="modalActions" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                    <button
                      className="saveBtn"
                      onClick={closeImportModal}
                      style={{
                        padding: "12px 24px",
                        background: "linear-gradient(135deg, #005fa3 0%, #0077c8 100%)",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff"
                      }}
                    >
                      Cerrar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* MODAL EXPORTAR MÉTRICAS */}
        {showExportModal && (
          <div className="modalOverlay" onClick={() => setShowExportModal(false)}>
            <div className="modalContent exportModal" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h3 style={{ color: "#005fa3", margin: 0, fontSize: "24px", fontWeight: "600" }}>
                  <Download style={{ verticalAlign: "middle", marginRight: "8px" }} />
                  Exportar Métricas
                </h3>
                <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
                  Selecciona las métricas que deseas exportar
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  padding: "12px",
                  background: "#e3f2fd",
                  borderRadius: "6px"
                }}>
                  <span style={{ fontSize: "14px", fontWeight: "600", color: "#005fa3" }}>
                    Todas las métricas
                  </span>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => toggleAllMetrics(true)}
                      style={{
                        padding: "6px 12px",
                        background: "#fff",
                        border: "1px solid #0077c8",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#0077c8"
                      }}
                    >
                      Seleccionar todas
                    </button>
                    <button
                      onClick={() => toggleAllMetrics(false)}
                      style={{
                        padding: "6px 12px",
                        background: "#fff",
                        border: "1px solid #999",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#666"
                      }}
                    >
                      Deseleccionar todas
                    </button>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.generalMetrics ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.generalMetrics}
                      onChange={() => toggleMetric("generalMetrics")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Métricas generales
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Total usuarios, espacios, usuarios activos y actividad de hoy
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.roleDistribution ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.roleDistribution}
                      onChange={() => toggleMetric("roleDistribution")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Distribución por rol
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Cantidad de usuarios por rol (admin, empleado, temporal)
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.spaceActivity ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.spaceActivity}
                      onChange={() => toggleMetric("spaceActivity")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Actividad de espacios
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Total de posts y comentarios por espacio
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.trafficData ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.trafficData}
                      onChange={() => toggleMetric("trafficData")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Tráfico de plataforma
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Actividad de usuarios y posts en los últimos 7 días
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.areaDistribution ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.areaDistribution}
                      onChange={() => toggleMetric("areaDistribution")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Distribución por área
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Cantidad de usuarios por área de trabajo
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: "#f8f9fa",
                    borderRadius: "6px",
                    cursor: "pointer",
                    border: selectedMetrics.ageDistribution ? "2px solid #0077c8" : "2px solid transparent",
                    transition: "all 0.3s"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedMetrics.ageDistribution}
                      onChange={() => toggleMetric("ageDistribution")}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                        Distribución por edad
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Rangos de edad de los usuarios
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="modalActions" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  className="cancelBtn"
                  onClick={() => setShowExportModal(false)}
                  style={{
                    padding: "12px 24px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#666"
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="saveBtn"
                  onClick={confirmExportMetrics}
                  disabled={!Object.values(selectedMetrics).some(v => v)}
                  style={{
                    padding: "12px 24px",
                    background: !Object.values(selectedMetrics).some(v => v) ? "#ccc" : "linear-gradient(135deg, #005fa3 0%, #0077c8 100%)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: !Object.values(selectedMetrics).some(v => v) ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <Download fontSize="small" />
                  Exportar Selección
                </button>
              </div>
            </div>
          </div>
        )}

        {showPwdModal && (
          <div className="modalOverlay" onClick={() => setShowPwdModal(false)}>
            <div className="modalContent pwdModal" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h3>Cambiar Contraseña</h3>
                <p>Ingresa y confirma la nueva contraseña</p>
              </div>
              <div className="modalForm">
                <input
                  type="password"
                  name="adminNewPassword"
                  placeholder="Nueva contraseña (mín. 8, con mayúscula, minúscula y número)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && changePassword()}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                />
                <input
                  type="password"
                  name="adminConfirmPassword"
                  placeholder="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && changePassword()}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
                <small className="formHint">Mínimo 8 caracteres, con mayúscula, minúscula y número, y coincidir en ambos campos.</small>
              </div>
              <div className="modalActions">
                <button className="cancelBtn" onClick={() => setShowPwdModal(false)}>
                  Cancelar
                </button>
                <button className="saveBtn" onClick={changePassword} disabled={passwordChanging}>
                  Guardar Contraseña
                </button>
              </div>
            </div>
          </div>
        )}

        {showResetModal && (
          <div className="modalOverlay" onClick={() => !resettingPassword && setShowResetModal(false)}>
            <div className="modalContent pwdModal" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h3>Resetear contraseña</h3>
                <p>Se cambiará la contraseña del usuario a su documento de identidad.</p>
              </div>
              <div className="modalActions">
                <button className="cancelBtn" onClick={() => setShowResetModal(false)} disabled={resettingPassword}>
                  Cancelar
                </button>
                <button className="saveBtn" onClick={confirmResetPassword} disabled={resettingPassword}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}

        {showOperationModal && (
          <div className="modalOverlay" onClick={() => setShowOperationModal(false)}>
            <div className={`modalContent operationModal ${operationModalData.isError ? "error" : "success"}`} onClick={(e) => e.stopPropagation()}>
              <h3>{operationModalData.title}</h3>
              <p>{operationModalData.message}</p>
              <div className="modalActions" style={{ justifyContent: "flex-end" }}>
                <button className="saveBtn" onClick={() => setShowOperationModal(false)}>
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        )}

        {showSpaceModal && (
          <div className="modalOverlay" onClick={() => setShowSpaceModal(false)}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <h3>
                {editingSpaceId ? "Editar Espacio" : "Crear Espacio"}
              </h3>
              {/* Los tres campos llevan etiqueta propia: antes solo tenían
                  placeholder, que desaparece al escribir y deja el formulario
                  sin decir qué es cada casilla. La imagen usaba el selector de
                  archivos nativo del navegador ("Choose File · No file
                  chosen"), el único control de toda la plataforma sin el
                  estilo del resto. */}
              <div className="modalForm spaceForm">
                <div className="campo">
                  <label htmlFor="espacioNombre">Nombre del espacio</label>
                  <input
                    id="espacioNombre"
                    name="name"
                    placeholder="Por ejemplo: Talento Humano"
                    value={spaceForm.name}
                    onChange={handleSpaceFormChange}
                  />
                </div>

                <div className="campo">
                  <label htmlFor="espacioDescripcion">Descripción</label>
                  <textarea
                    id="espacioDescripcion"
                    name="description"
                    placeholder="De qué trata este espacio"
                    value={spaceForm.description}
                    onChange={handleSpaceFormChange}
                  />
                </div>

                <div className="campo">
                  <label>Imagen de portada</label>
                  <label
                    className="dropzone dropzone--compacta"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add("dragging");
                    }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("dragging")}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("dragging");
                      const archivo = e.dataTransfer.files?.[0];
                      if (archivo && archivo.type.startsWith("image/")) {
                        setSpaceForm((prev) => ({
                          ...prev,
                          imageFile: archivo,
                          imagePreview: URL.createObjectURL(archivo),
                        }));
                      }
                    }}
                  >
                    <FileUpload className="dropzone__icono" />
                    <span className="dropzone__titulo">
                      {spaceForm.imageFile ? spaceForm.imageFile.name : "Selecciona o arrastra una imagen"}
                    </span>
                    <span className="dropzone__nota">JPG, PNG o WEBP</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const archivo = e.target.files[0];
                        setSpaceForm((prev) => ({
                          ...prev,
                          imageFile: archivo,
                          imagePreview: archivo ? URL.createObjectURL(archivo) : prev.imagePreview,
                        }));
                      }}
                    />
                  </label>

                  {spaceForm.imagePreview && (
                    <img className="spaceForm__preview" src={spaceForm.imagePreview} alt="Vista previa de la portada" />
                  )}
                </div>
              </div>
              <div className="modalActions">
                <button className="cancelBtn" onClick={() => setShowSpaceModal(false)}>
                  Cancelar
                </button>
                <button className="saveBtn" onClick={() => setShowConfirmSaveSpaceModal(true)}>
                  {editingSpaceId ? "Guardar Cambios" : "Crear Espacio"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteSpaceModal && (
          <div className="modalOverlay" onClick={() => setShowDeleteSpaceModal(false)}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <h3>Eliminar Espacio</h3>
              <p>¿Estás seguro de que deseas eliminar el espacio <strong>{deleteSpaceName}</strong>?</p>
              <p style={{ color: '#dc3545', fontSize: '0.9rem', marginBottom: '16px', fontWeight: 500 }}>Esta acción no se puede deshacer.</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="cancelBtn" onClick={() => setShowDeleteSpaceModal(false)}>Cancelar</button>
                <button className="deleteConfirmBtn" onClick={confirmDeleteSpace}>Eliminar Espacio</button>
              </div>
            </div>
          </div>
        )}
        {showConfirmSaveSpaceModal && (
          <div className="modalOverlay" onClick={() => setShowConfirmSaveSpaceModal(false)}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <h3>{editingSpaceId ? "Confirmar cambios" : "Confirmar creación"}</h3>
              <p>{editingSpaceId ? `¿Guardar cambios en "${spaceForm.name}"?` : `¿Crear el espacio "${spaceForm.name}"?`}</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="cancelBtn" onClick={() => setShowConfirmSaveSpaceModal(false)}>Cancelar</button>
                <button className="saveBtn" onClick={handleConfirmSaveSpace}>Confirmar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}